import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { agriculturalAPI } from '../services/api';
import {
  Database,
  Download,
  Calendar,
  Tag,
  RefreshCw,
  AlertCircle,
  X,
  Search,
  Check,
  CheckSquare,
  Square,
  Filter,
  Maximize2,
  Globe,
  Trophy,
  MapPin,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { RootState } from '../store/store';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MapContainer, TileLayer, GeoJSON, useMap, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map size invalidation when modal opens
const MapUpdater: React.FC<{ isExpanded: boolean }> = ({ isExpanded }) => {
  const map = useMap();
  
  useEffect(() => {
    if (isExpanded) {
      // Invalidate size after a short delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        map.invalidateSize();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, map]);
  
  return null;
};

interface FilterFieldConfig {
  fieldName: string;
  displayName: string;
  fieldType: 'keyword' | 'text' | 'boolean' | 'integer' | 'date';
  required: boolean;
  multiSelect: boolean;
  priority: number;
  isAreaField: boolean;
  isYearField: boolean;
  isValueField: boolean;
  isItemLike?: boolean;
}

interface AvailableFilterField {
  fieldName: string;
  fieldType: string;
  valueCount: number;
}

interface Index {
  name: string;
  displayName: string;
  title?: string;
  description: string;
  icon: string;
  filterFields: FilterFieldConfig[];
  availableFilterFields: AvailableFilterField[];
  documentCount: number;
  status: 'available' | 'not_available';
  hasMetadata: boolean;
}

interface FilterValue {
  value: string;
  count: number;
}

interface VisualizationFilters {
  index: string;
  fields: Record<string, string | string[]>; // Dynamic filter fields from metadata
  fromYear?: string;
  toYear?: string;
  aggregation?: 'average' | 'sum' | 'min' | 'max';
}

interface ChartDataPoint {
  name: string;
  value: number;
  [key: string]: any;
}

interface MapData {
  country: string;
  value: number;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

// Country name mapping between FAOSTAT names and GeoJSON names
const COUNTRY_NAME_MAPPING: { [key: string]: string[] } = {
  'tunisia': ['tunisia', 'tunisie'],
  'china, mainland': ['china', 'peoples republic of china', 'pr china'],
  'greece': ['greece', 'hellenic republic'],
  'syrian arab republic': ['syria', 'syrian arab republic'],
  'romania': ['romania'],
  'spain': ['spain', 'espana'],
  'algeria': ['algeria', 'algerie'],
  'italy': ['italy', 'italia'],
  'sudan': ['sudan'],
  'somalia': ['somalia'],
  'russian federation': ['russia', 'russian federation'],
  'turkey': ['turkey', 'turkiye', 'türkiye'],
  'türkiye': ['turkey', 'turkiye', 'türkiye'],
  'morocco': ['morocco', 'maroc'],
  'egypt': ['egypt', 'arab republic of egypt'],
  'france': ['france'],
  'germany': ['germany', 'deutschland'],
  'united kingdom': ['united kingdom', 'uk', 'great britain'],
  'united states': ['united states', 'usa', 'united states of america'],
  'brazil': ['brazil', 'brasil'],
  'india': ['india'],
  'indonesia': ['indonesia'],
  'japan': ['japan'],
  'south korea': ['south korea', 'korea', 'republic of korea'],
  'australia': ['australia'],
  'canada': ['canada'],
  'mexico': ['mexico'],
  'argentina': ['argentina'],
  'south africa': ['south africa'],
  'nigeria': ['nigeria'],
  'kenya': ['kenya'],
  'ethiopia': ['ethiopia'],
  'pakistan': ['pakistan'],
  'bangladesh': ['bangladesh'],
  'vietnam': ['vietnam', 'viet nam'],
  'thailand': ['thailand'],
  'philippines': ['philippines'],
  'malaysia': ['malaysia'],
  'saudi arabia': ['saudi arabia'],
  'iran': ['iran', 'islamic republic of iran'],
  'iraq': ['iraq'],
  'israel': ['israel'],
  'ukraine': ['ukraine'],
  'poland': ['poland', 'polska'],
  'netherlands': ['netherlands', 'holland'],
  'belgium': ['belgium', 'belgie'],
  'portugal': ['portugal'],
  'sweden': ['sweden', 'sverige'],
  'norway': ['norway', 'norge'],
  'denmark': ['denmark', 'danmark'],
  'finland': ['finland', 'suomi'],
  'switzerland': ['switzerland', 'schweiz'],
  'austria': ['austria', 'osterreich'],
  'czech republic': ['czech republic', 'czechia'],
  'hungary': ['hungary', 'magyarorszag'],
  'chile': ['chile'],
  'colombia': ['colombia'],
  'peru': ['peru'],
  'venezuela': ['venezuela'],
  'new zealand': ['new zealand'],
};

const DataVisualization: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [indices, setIndices] = useState<Index[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<string>('');
  const [selectedIndexMeta, setSelectedIndexMeta] = useState<Index | null>(null);
  const [filterFieldConfigs, setFilterFieldConfigs] = useState<FilterFieldConfig[]>([]);
  const [filterValues, setFilterValues] = useState<{ [key: string]: FilterValue[] }>({});
  const [filters, setFilters] = useState<VisualizationFilters>({
    index: '',
    fields: {},
    aggregation: 'average',
  });
  
  // Data states
  const [mapData, setMapData] = useState<MapData[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [countryTimeSeries, setCountryTimeSeries] = useState<{ [country: string]: ChartDataPoint[] }>({});
  // New structure: element -> country -> time series data
  const [elementTimeSeries, setElementTimeSeries] = useState<{ [element: string]: { [country: string]: ChartDataPoint[] } }>({});
  const [allResults, setAllResults] = useState<any[]>([]);
  const [globalResults, setGlobalResults] = useState<any[]>([]); // Global data without area filter for treemap
  const [worldGeoJSON, setWorldGeoJSON] = useState<any>(null);
  const [countryStats, setCountryStats] = useState<{ [country: string]: any }>({});
  const [showCountrySelector, setShowCountrySelector] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [elementSearchTerm, setElementSearchTerm] = useState('');
  const [expandedChart, setExpandedChart] = useState<{ type: 'element' | 'country', key: string, title: string, subtitle: string, chartData: any[], dataKeys: string[], colors: string[] } | null>(null);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<string>('all');
  const [expandedMap, setExpandedMap] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedTopProducers, setExpandedTopProducers] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load world countries GeoJSON
  useEffect(() => {
    const loadWorldGeoJSON = async () => {
      try {
        // Using a simplified world countries GeoJSON from a CDN
        const response = await fetch('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson');
        if (response.ok) {
          const data = await response.json();
          setWorldGeoJSON(data);
        } else {
          // Fallback: create a simple GeoJSON structure
          console.warn('Could not load world GeoJSON, using fallback');
        }
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
        // Try alternative source
        try {
          const response = await fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json');
          if (response.ok) {
            const topojson = await response.json();
            // Convert topojson to geojson would require topojson library
            // For now, we'll use the first source or create minimal structure
          }
        } catch (e) {
          console.error('Error loading alternative GeoJSON:', e);
        }
      }
    };
    loadWorldGeoJSON();
  }, []);

  // Load indices on mount
  useEffect(() => {
    loadIndices();
  }, []);

  // Load filter values when index changes
  useEffect(() => {
    if (selectedIndex) {
      loadAllFilterValues(selectedIndex);
      setFilters(prev => ({ ...prev, index: selectedIndex }));
    }
  }, [selectedIndex]);

  const loadIndices = async () => {
    try {
      const response = await agriculturalAPI.getIndices();
      if (response.data.success) {
        setIndices(response.data.data);
        // Prioritize indices with metadata (have filterFields configured)
        const configuredIndex = response.data.data.find((index: Index) => 
          index.hasMetadata && index.filterFields?.length > 0 && index.status === 'available'
        );
        
        if (configuredIndex) {
          setSelectedIndex(configuredIndex.name);
        } else {
          // Fallback to first available index
          const availableIndex = response.data.data.find((index: Index) => index.status === 'available');
          if (availableIndex) {
            setSelectedIndex(availableIndex.name);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load indices:', err);
      setError('Failed to load available indices');
    }
  };

  const loadAllFilterValues = async (indexName: string) => {
    try {
      const allFilterValues: { [key: string]: FilterValue[] } = {};
      
      // Get the index metadata from the loaded indices
      const indexMeta = indices.find(i => i.name === indexName);
      setSelectedIndexMeta(indexMeta || null);
      
      // Use filterFields from metadata if available
      const filterFields = indexMeta?.filterFields || [];
      setFilterFieldConfigs(filterFields);
      
      // If no filterFields configured, try auto-configure first
      if (filterFields.length === 0) {
        try {
          console.log(`No filterFields configured for ${indexName}, auto-configuring...`);
          const autoRes = await agriculturalAPI.autoConfigureIndex(indexName);
          if (autoRes.data.success && autoRes.data.data?.filterFields?.length > 0) {
            setFilterFieldConfigs(autoRes.data.data.filterFields);
            // Update the index meta locally
            if (indexMeta) {
              indexMeta.filterFields = autoRes.data.data.filterFields;
              setSelectedIndexMeta({ ...indexMeta });
            }
          }
        } catch (e) {
          console.warn('Auto-configure failed, will use available filter fields:', e);
        }
      }

      // Load all filter values from MongoDB (primary source)
      try {
        const response = await agriculturalAPI.getAllIndexFilterValues(indexName);
        if (response.data.success) {
          for (const fieldName of Object.keys(response.data.data.filterValues)) {
            const values = response.data.data.filterValues[fieldName]?.values || [];
            const validValues = values.filter((v: FilterValue) => 
              v.value && v.value.trim() !== '' && v.value.toLowerCase() !== 'value'
            );
            if (validValues.length > 0) {
              allFilterValues[fieldName] = validValues;
            }
          }
        }
      } catch (err) {
        console.error('Failed to load filter values from MongoDB:', err);
      }

      // If we have filterFieldConfigs but some fields are missing values, try terms endpoint
      const currentConfigs = filterFields.length > 0 ? filterFields : filterFieldConfigs;
      for (const fieldConfig of currentConfigs) {
        if (fieldConfig.isValueField || fieldConfig.isYearField) continue; // Skip value/year fields
        
        // Check if we already have values for this field (try both original and lowercase)
        const hasValues = (allFilterValues[fieldConfig.fieldName]?.length > 0) || 
                          (allFilterValues[fieldConfig.fieldName.toLowerCase()]?.length > 0);
        
        if (!hasValues) {
          try {
            const termsRes = await agriculturalAPI.getIndexTerms(indexName, {
              field: fieldConfig.fieldName,
              size: 300,
            });
            if (termsRes.data.success && termsRes.data.data && Array.isArray(termsRes.data.data)) {
              const validTerms = termsRes.data.data.filter((term: FilterValue) => 
                term.value && term.value.trim() !== ''
              );
              if (validTerms.length > 0) {
                allFilterValues[fieldConfig.fieldName] = validTerms;
              }
            }
          } catch (e) {
            console.warn(`Failed to load terms for field ${fieldConfig.fieldName}:`, e);
          }
        }
      }

      // If still no filterFieldConfigs and no filter values, discover from availableFilterFields
      if (currentConfigs.length === 0 && Object.keys(allFilterValues).length === 0) {
        const availableFields = indexMeta?.availableFilterFields || [];
        if (availableFields.length > 0) {
          // Load values for each available field
          for (const field of availableFields) {
            if (allFilterValues[field.fieldName]?.length > 0) continue;
            try {
              const fieldRes = await agriculturalAPI.getIndexFilterValues(indexName, field.fieldName, 300);
              if (fieldRes.data.success && fieldRes.data.data?.values) {
                const validVals = fieldRes.data.data.values.filter((v: FilterValue) => 
                  v.value && v.value.trim() !== '' && v.value.toLowerCase() !== 'value'
                );
                if (validVals.length > 0) {
                  allFilterValues[field.fieldName] = validVals;
                }
              }
            } catch (e) {
              console.warn(`Failed to load filter values for ${field.fieldName}:`, e);
            }
          }
        }
      }

      setFilterValues(allFilterValues);
    } catch (err) {
      console.error('Failed to load filter values:', err);
      setFilterValues({});
    }
  };

  // Auto-load initial data when filters are ready
  useEffect(() => {
    if (selectedIndex && Object.keys(filterValues).length > 0 && mapData.length === 0 && Object.keys(filters.fields).length === 0) {
      const currentConfigs = filterFieldConfigs.length > 0 ? filterFieldConfigs : 
        (selectedIndexMeta?.filterFields || []);
      
      const newFields: Record<string, string | string[]> = {};
      
      if (currentConfigs.length > 0) {
        for (const fieldConfig of currentConfigs) {
          if (fieldConfig.isValueField || fieldConfig.isYearField) continue;
          
          const fieldValues = filterValues[fieldConfig.fieldName] || filterValues[fieldConfig.fieldName.toLowerCase()] || [];
          if (fieldValues.length > 0) {
            if (fieldConfig.multiSelect) {
              newFields[fieldConfig.fieldName] = [fieldValues[0].value];
            } else {
              newFields[fieldConfig.fieldName] = fieldValues[0].value;
            }
          }
        }
      } else {
        // Fallback: auto-select first value for any available filter field
        for (const [fieldName, values] of Object.entries(filterValues)) {
          if (values.length > 0) {
            newFields[fieldName] = values[0].value;
          }
        }
      }
      
      if (Object.keys(newFields).length > 0) {
        setFilters(prev => ({ ...prev, index: selectedIndex, fields: newFields }));
      }
    }
  }, [filterValues, selectedIndex]);

  // Auto-visualize when default filters are set
  useEffect(() => {
    const hasRequiredFilters = Object.values(filters.fields).some(v => 
      Array.isArray(v) ? v.length > 0 : !!v
    );
    if (selectedIndex && hasRequiredFilters && mapData.length === 0 && !loading) {
      setInitialLoading(true);
      const timer = setTimeout(() => {
        handleVisualize();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [JSON.stringify(filters.fields), selectedIndex]);

  const handleFilterChange = (fieldName: string, value: string | string[]) => {
    // Handle special non-dynamic fields
    if (fieldName === 'fromYear' || fieldName === 'toYear' || fieldName === 'aggregation') {
      setFilters(prev => ({ ...prev, [fieldName]: value as string }));
    } else if (value === '__all__') {
      // "All" selected — set to all available values for this field
      const allValues = (filterValues[fieldName] || filterValues[fieldName.toLowerCase()] || []).map((fv: FilterValue) => fv.value);
      setFilters(prev => ({ ...prev, fields: { ...prev.fields, [fieldName]: allValues } }));
    } else {
      // Handle dynamic filter fields
      setFilters(prev => ({ ...prev, fields: { ...prev.fields, [fieldName]: value } }));
    }
  };

  const handleAreaChange = (selectedAreas: string[]) => {
    const areaField = filterFieldConfigs.find(f => f.isAreaField)?.fieldName;
    if (areaField) {
      setFilters(prev => ({ ...prev, fields: { ...prev.fields, [areaField]: selectedAreas } }));
    }
  };

  const handleElementChange = (selectedElements: string[]) => {
    // Find the first element-like field (keyword field that's not area/year/value)
    const elementFields = filterFieldConfigs.filter(f => 
      !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword'
    );
    const primaryElementField = elementFields.length > 0 ? elementFields[0].fieldName : null;
    if (primaryElementField) {
      setFilters(prev => ({ ...prev, fields: { ...prev.fields, [primaryElementField]: selectedElements } }));
    }
  };

  const handleVisualize = async () => {
    if (!selectedIndex) {
      setError('Please select an index');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Normalize field names to match filterFieldConfigs casing
      // This prevents duplicate params like item=Tomatoes&Item=Wheat
      const normalizedFields: Record<string, string | string[]> = {};
      const fieldConfigMap = new Map<string, string>();
      for (const fc of filterFieldConfigs) {
        fieldConfigMap.set(fc.fieldName.toLowerCase(), fc.fieldName);
      }
      for (const [fieldName, value] of Object.entries(filters.fields)) {
        const normalizedName = fieldConfigMap.get(fieldName.toLowerCase()) || fieldName;
        if (normalizedFields[normalizedName]) {
          // Merge values if the normalized key already exists
          const existing = Array.isArray(normalizedFields[normalizedName]) 
            ? normalizedFields[normalizedName] as string[] 
            : [normalizedFields[normalizedName] as string];
          const incoming = Array.isArray(value) ? value : [value as string];
          normalizedFields[normalizedName] = Array.from(new Set([...existing, ...incoming]));
        } else {
          normalizedFields[normalizedName] = value;
        }
      }

      const params: any = { limit: 10000 };
      
      // Build search params dynamically from normalized filter fields
      // Skip fields where all values are selected (equivalent to no filter)
      for (const [fieldName, value] of Object.entries(normalizedFields)) {
        if (value) {
          // Check if all values for this field are selected — if so, skip the filter
          const fieldVals = filterValues[fieldName] || [];
          const allFieldValues = fieldVals.map((fv: FilterValue) => fv.value);
          if (Array.isArray(value) && allFieldValues.length > 0 && allFieldValues.every(v => value.includes(v))) {
            // All values selected — skip this filter (same as no filter)
            continue;
          }
          
          if (Array.isArray(value) && value.length > 0) {
            params[fieldName] = value;
          } else if (typeof value === 'string' && value.trim()) {
            params[fieldName] = value;
          }
        }
      }

      // Add year filters if present
      if (filters.fromYear) params.fromYear = filters.fromYear;
      if (filters.toYear) params.toYear = filters.toYear;

      const response = await agriculturalAPI.searchIndex(selectedIndex, params);
      
      // Build global params (without area-like fields) for treemap/global view
      const globalParams: any = { limit: 10000 };
      const areaField = filterFieldConfigs.find(f => f.isAreaField)?.fieldName;
      for (const [fieldName, value] of Object.entries(normalizedFields)) {
        if (fieldName === areaField) continue; // Skip area for global data
        if (value) {
          // Skip fields where all values are selected
          const fieldVals = filterValues[fieldName] || [];
          const allFieldValues = fieldVals.map((fv: FilterValue) => fv.value);
          if (Array.isArray(value) && allFieldValues.length > 0 && allFieldValues.every(v => value.includes(v))) {
            continue;
          }
          
          if (Array.isArray(value) && value.length > 0) {
            globalParams[fieldName] = value;
          } else if (typeof value === 'string' && value.trim()) {
            globalParams[fieldName] = value;
          }
        }
      }
      if (filters.fromYear) globalParams.fromYear = filters.fromYear;
      if (filters.toYear) globalParams.toYear = filters.toYear;
      
      let globalDataResults: any[] = [];
      try {
        const globalResponse = await agriculturalAPI.searchIndex(selectedIndex, globalParams);
        if (globalResponse.data.success) {
          globalDataResults = globalResponse.data.data;
          setGlobalResults(globalDataResults);
        }
      } catch (globalErr) {
        console.warn('Failed to fetch global data for treemap:', globalErr);
        setGlobalResults([]);
      }
      
      if (response.data.success) {
        const results = response.data.data;
        setAllResults(results);
        
        // Check if the index is empty (no documents)
        if (response.data.meta?.documentCount === 0 || (results.length === 0 && response.data.meta?.message)) {
          setError(response.data.meta?.message || 'This index has no documents. Import data to start visualizing.');
          setMapData([]);
          setSelectedCountries([]);
          setCountryTimeSeries({});
          setElementTimeSeries({});
          setCountryStats({});
          setInitialLoading(false);
          setLoading(false);
          return;
        }
        
        // Transform data for different visualizations
        const transformedData = transformDataForVisualization(results, filters);
        
        // Set map data (by country/area)
        setMapData(transformedData.mapData);
        
        // Set country time series (if area is selected in filters)
        if (areaField && filters.fields[areaField]) {
          const areas = Array.isArray(filters.fields[areaField]) 
            ? filters.fields[areaField] as string[]
            : [filters.fields[areaField] as string];
          setSelectedCountries(areas);
          updateTimeSeriesForCountries(areas);
        } else {
          setSelectedCountries([]);
          setCountryTimeSeries({});
          setCountryStats({});
        }
        
        // Note: regionShare, topProducers, and topCommodities are no longer used
        // Data is now organized by element and country in elementTimeSeries
      } else {
        setError(response.data.message || 'Visualization failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Visualization failed');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const transformDataForVisualization = (results: any[], filters: VisualizationFilters) => {
    if (results.length === 0) {
      return {
        mapData: [],
        timeSeries: [],
        regionShare: [],
        topProducers: [],
        topCommodities: [],
      };
    }

    const aggregationFunc = filters.aggregation || 'average';
    
    // Helper function to aggregate
    const aggregate = (values: number[]) => {
      if (values.length === 0) return 0;
      switch (aggregationFunc) {
        case 'sum':
          return values.reduce((a, b) => a + b, 0);
        case 'min':
          return Math.min(...values);
        case 'max':
          return Math.max(...values);
        case 'average':
        default:
          return values.reduce((a, b) => a + b, 0) / values.length;
      }
    };

    // Detect field roles from filterFieldConfigs
    const areaField = filterFieldConfigs.find(f => f.isAreaField)?.fieldName;
    const yearField = filterFieldConfigs.find(f => f.isYearField)?.fieldName;
    const valueField = filterFieldConfigs.find(f => f.isValueField)?.fieldName;
    
    // Detect element-like and item-like fields (non-area, non-year, non-value keyword fields)
    const elementFields = filterFieldConfigs.filter(f => 
      !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword'
    );
    const primaryElementField = elementFields.length > 0 ? elementFields[0].fieldName : null;
    const primaryItemField = elementFields.length > 1 ? elementFields[1].fieldName : null;

    // Helper to get field value from result, trying both original and lowercase
    const getFieldValue = (result: any, fieldName: string): string => {
      return result[fieldName] || result[fieldName.toLowerCase()] || 
             result[fieldName.charAt(0).toUpperCase() + fieldName.slice(1)] || '';
    };

    // Group by area (country) AND element for map
    const byCountryElement: { [key: string]: { [element: string]: number[] } } = {};
    const byCountryYear: { [key: string]: { [year: string]: number[] } } = {};
    const byRegion: { [key: string]: number[] } = {};
    const byCommodity: { [key: string]: number[] } = {};

    // Get the primary element to use for map
    const selectedElementValues = primaryElementField 
      ? (Array.isArray(filters.fields[primaryElementField]) 
          ? filters.fields[primaryElementField] as string[]
          : filters.fields[primaryElementField] ? [filters.fields[primaryElementField] as string] : [])
      : [];
    const primaryElement = selectedElementValues.length > 0 ? selectedElementValues[0] : null;

    // Get selected area values
    const selectedAreaValues = areaField
      ? (Array.isArray(filters.fields[areaField])
          ? filters.fields[areaField] as string[]
          : filters.fields[areaField] ? [filters.fields[areaField] as string] : [])
      : [];

    results.forEach(result => {
      const area = areaField ? getFieldValue(result, areaField) : (result.area || result.Area || 'Unknown');
      const year = yearField ? getFieldValue(result, yearField) : (result.year || result.Year || '');
      const value = parseFloat(valueField ? getFieldValue(result, valueField) : (result.value || result.Value || '0'));
      const item = primaryItemField ? getFieldValue(result, primaryItemField) : (result.item || result.Item || '');
      const element = primaryElementField ? getFieldValue(result, primaryElementField) : (result.element || result.Element || '');
      
      // Skip invalid data
      if (!area || area === 'Unknown' || isNaN(value) || value <= 0) {
        return;
      }
      
      // Filter by element if specified
      if (primaryElementField && filters.fields[primaryElementField]) {
        const elements = Array.isArray(filters.fields[primaryElementField]) 
          ? filters.fields[primaryElementField] as string[] 
          : [filters.fields[primaryElementField] as string];
        if (!elements.includes(element)) {
          return;
        }
      }

      // Map data by country AND element
      if (!byCountryElement[area]) byCountryElement[area] = {};
      if (!byCountryElement[area][element]) byCountryElement[area][element] = [];
      byCountryElement[area][element].push(value);

      // Time series data
      if (year && selectedAreaValues.length > 0) {
        if (selectedAreaValues.includes(area)) {
          if (!byCountryYear[area]) byCountryYear[area] = {};
          if (!byCountryYear[area][year]) byCountryYear[area][year] = [];
          byCountryYear[area][year].push(value);
        }
      }

      // Region data
      const region = getRegion(area);
      if (!byRegion[region]) byRegion[region] = [];
      byRegion[region].push(value);

      // Top commodities (if area is selected)
      if (selectedAreaValues.length > 0 && item && selectedAreaValues.includes(area)) {
        if (!byCommodity[item]) byCommodity[item] = [];
        byCommodity[item].push(value);
      }
    });

    // Transform to chart data
    const mapData: MapData[] = Object.keys(byCountryElement).map(country => {
      let valuesToUse: number[] = [];
      
      if (primaryElement && byCountryElement[country][primaryElement]) {
        valuesToUse = byCountryElement[country][primaryElement];
      } else {
        const firstElement = Object.keys(byCountryElement[country])[0];
        if (firstElement) {
          valuesToUse = byCountryElement[country][firstElement];
        }
      }
      
      return {
        country,
        value: valuesToUse.length > 0 ? aggregate(valuesToUse) : 0,
      };
    }).filter(d => d.value > 0);

    // Time series for multiple areas
    const timeSeries: ChartDataPoint[] = [];
    if (selectedAreaValues.length > 0) {
      const allYears = new Set<string>();
      selectedAreaValues.forEach(area => {
        if (byCountryYear[area]) {
          Object.keys(byCountryYear[area]).forEach(year => allYears.add(year));
        }
      });
      const sortedYears = Array.from(allYears).sort();
      sortedYears.forEach(year => {
        const yearValues: number[] = [];
        selectedAreaValues.forEach(area => {
          if (byCountryYear[area] && byCountryYear[area][year]) {
            yearValues.push(...byCountryYear[area][year]);
          }
        });
        if (yearValues.length > 0) {
          timeSeries.push({
            name: year,
            value: aggregate(yearValues),
          });
        }
      });
    }

    const regionShare: ChartDataPoint[] = Object.keys(byRegion).map(region => ({
      name: region,
      value: aggregate(byRegion[region]),
    }));

    const topProducers: ChartDataPoint[] = Object.keys(byCountryElement)
      .map(country => {
        let valuesToUse: number[] = [];
        if (primaryElement && byCountryElement[country][primaryElement]) {
          valuesToUse = byCountryElement[country][primaryElement];
        } else {
          const firstElement = Object.keys(byCountryElement[country])[0];
          if (firstElement) {
            valuesToUse = byCountryElement[country][firstElement];
          }
        }
        return {
          name: country,
          value: valuesToUse.length > 0 ? aggregate(valuesToUse) : 0,
        };
      })
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const topCommodities: ChartDataPoint[] = selectedAreaValues.length > 0
      ? Object.keys(byCommodity)
          .map(item => ({
            name: item,
            value: aggregate(byCommodity[item]),
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10)
      : [];

    return {
      mapData,
      timeSeries,
      regionShare,
      topProducers,
      topCommodities,
    };
  };

  // Comprehensive continent mapping
  const CONTINENT_MAPPING: { [continent: string]: string[] } = {
    'Africa': [
      'Tunisia', 'Algeria', 'Sudan', 'Somalia', 'Morocco', 'Egypt', 'South Africa', 'Nigeria', 
      'Kenya', 'Ethiopia', 'Ghana', 'Tanzania', 'Uganda', 'Angola', 'Mozambique', 'Madagascar',
      'Cameroon', 'Côte d\'Ivoire', 'Niger', 'Burkina Faso', 'Mali', 'Malawi', 'Zambia', 'Senegal',
      'Chad', 'Zimbabwe', 'Guinea', 'Rwanda', 'Benin', 'Burundi', 'Tunisia', 'Togo', 'Sierra Leone',
      'Libya', 'Mauritania', 'Liberia', 'Central African Republic', 'Eritrea', 'Gambia', 'Botswana',
      'Namibia', 'Gabon', 'Lesotho', 'Guinea-Bissau', 'Equatorial Guinea', 'Mauritius', 'Eswatini',
      'Djibouti', 'Comoros', 'Cabo Verde', 'São Tomé and Príncipe', 'Seychelles'
    ],
    'Asia': [
      'China, mainland', 'China', 'India', 'Indonesia', 'Pakistan', 'Bangladesh', 'Japan', 'Philippines',
      'Vietnam', 'Thailand', 'Myanmar', 'South Korea', 'Iraq', 'Afghanistan', 'Saudi Arabia', 'Uzbekistan',
      'Malaysia', 'Yemen', 'Nepal', 'North Korea', 'Sri Lanka', 'Kazakhstan', 'Syrian Arab Republic',
      'Cambodia', 'Jordan', 'Azerbaijan', 'United Arab Emirates', 'Tajikistan', 'Laos', 'Israel',
      'Lebanon', 'Kyrgyzstan', 'Turkmenistan', 'Singapore', 'Oman', 'State of Palestine', 'Kuwait',
      'Georgia', 'Mongolia', 'Armenia', 'Qatar', 'Bahrain', 'Timor-Leste', 'Cyprus', 'Bhutan', 'Maldives',
      'Brunei', 'Macao', 'Hong Kong'
    ],
    'Europe': [
      'Russian Federation', 'Germany', 'United Kingdom', 'France', 'Italy', 'Spain', 'Ukraine', 'Poland',
      'Romania', 'Netherlands', 'Belgium', 'Greece', 'Czech Republic', 'Portugal', 'Sweden', 'Hungary',
      'Belarus', 'Austria', 'Switzerland', 'Bulgaria', 'Serbia', 'Denmark', 'Finland', 'Slovakia',
      'Norway', 'Ireland', 'Croatia', 'Bosnia and Herzegovina', 'Albania', 'Lithuania', 'Slovenia',
      'Latvia', 'Estonia', 'North Macedonia', 'Moldova', 'Luxembourg', 'Malta', 'Iceland', 'Montenegro',
      'Monaco', 'Liechtenstein', 'Andorra', 'San Marino', 'Vatican City'
    ],
    'Americas': [
      'United States', 'Brazil', 'Mexico', 'Colombia', 'Argentina', 'Canada', 'Peru', 'Venezuela',
      'Chile', 'Ecuador', 'Guatemala', 'Cuba', 'Haiti', 'Bolivia', 'Dominican Republic', 'Honduras',
      'Paraguay', 'Nicaragua', 'El Salvador', 'Costa Rica', 'Panama', 'Uruguay', 'Jamaica', 'Trinidad and Tobago',
      'Guyana', 'Suriname', 'Belize', 'Bahamas', 'Barbados', 'Saint Lucia', 'Grenada', 'Saint Vincent and the Grenadines',
      'Antigua and Barbuda', 'Dominica', 'Saint Kitts and Nevis'
    ],
    'Oceania': [
      'Australia', 'Papua New Guinea', 'New Zealand', 'Fiji', 'Solomon Islands', 'Vanuatu', 'New Caledonia',
      'French Polynesia', 'Samoa', 'Guam', 'Kiribati', 'Micronesia', 'Tonga', 'Marshall Islands',
      'Northern Mariana Islands', 'American Samoa', 'Palau', 'Cook Islands', 'Tuvalu', 'Nauru'
    ]
  };

  // Get continent for a country
  const getContinentForCountry = (country: string): string | null => {
    const countryLower = country.toLowerCase().trim();
    for (const [continent, countries] of Object.entries(CONTINENT_MAPPING)) {
      if (countries.some(c => {
        const cLower = c.toLowerCase().trim();
        return cLower === countryLower || 
               countryLower.includes(cLower) || 
               cLower.includes(countryLower) ||
               isCountryMatch(c, country);
      })) {
        return continent;
      }
    }
    return null;
  };

  const getRegion = (country: string): string => {
    return getContinentForCountry(country) || 'Other';
  };

  const getVisualizationTitle = () => {
    if (mapData.length === 0) return '';
    // Build title dynamically from filter fields
    const parts: string[] = [];
    const nonAreaFields = filterFieldConfigs.filter(f => !f.isAreaField && !f.isYearField && !f.isValueField);
    nonAreaFields.forEach(f => {
      const val = filters.fields[f.fieldName];
      if (val) {
        const display = Array.isArray(val) ? val.join(', ') : val;
        if (display) parts.push(display);
      }
    });
    // If no element-like fields, use index display name
    if (parts.length === 0) {
      parts.push(selectedIndexMeta?.title || selectedIndex || 'Data');
    }
    const itemFields = filterFieldConfigs.filter(f => !f.isAreaField && !f.isYearField && !f.isValueField && !nonAreaFields.includes(f));
    // Try to find an "item"-like field
    const itemField = filterFieldConfigs.find(f => f.isAreaField === false && f.isYearField === false && f.isValueField === false && f.fieldName.toLowerCase().includes('item'));
    if (itemField) {
      const itemVal = filters.fields[itemField.fieldName];
      if (itemVal) {
        const display = Array.isArray(itemVal) ? itemVal.join(', ') : itemVal;
        return `${parts.join(' - ')} of ${display} by country`;
      }
    }
    return `${parts.join(' - ')} by country`;
  };

  const getVisualizationSubtitle = () => {
    if (mapData.length === 0) return '';
    const aggregation = filters.aggregation || 'average';
    const period = filters.fromYear && filters.toYear 
      ? `${filters.fromYear} - ${filters.toYear}`
      : 'All Years';
    return `${aggregation.charAt(0).toUpperCase() + aggregation.slice(1)} ${period}`;
  };

  const getColorForValue = (value: number, maxValue: number) => {
    if (maxValue === 0) return '#fef3c7';
    const ratio = value / maxValue;
    if (ratio <= 0.2) return '#fef3c7'; // Lightest yellow
    if (ratio <= 0.4) return '#fde68a'; // Yellow
    if (ratio <= 0.6) return '#f59e0b'; // Orange
    if (ratio <= 0.8) return '#ef4444'; // Red
    return '#dc2626'; // Dark red
  };

  const maxMapValue = mapData.length > 0 ? Math.max(...mapData.map(d => d.value)) : 0;

  // Handle country click to add/remove from selection
  const handleCountryClick = (country: string) => {
    setSelectedCountries(prev => {
      if (prev.includes(country)) {
        // Remove country
        const newSelection = prev.filter(c => c !== country);
        updateTimeSeriesForCountries(newSelection);
        return newSelection;
      } else {
        // Add country (no limit, but warn if too many)
        const newSelection = [...prev, country];
        updateTimeSeriesForCountries(newSelection);
        return newSelection;
      }
    });
  };

  // Toggle country selection (for checkbox interface)
  const toggleCountrySelection = (country: string) => {
    handleCountryClick(country);
  };

  // Get filtered countries based on search and region
  const getFilteredCountries = () => {
    let filtered = mapData.map(d => d.country);
    
    // Filter by region
    if (selectedRegionFilter !== 'all') {
      filtered = filtered.filter(country => {
        const region = getRegion(country);
        return region === selectedRegionFilter;
      });
    }
    
    // Filter by search term
    if (countrySearchTerm.trim()) {
      const searchLower = countrySearchTerm.toLowerCase();
      filtered = filtered.filter(country => 
        country.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered.sort();
  };

  // Get unique regions from map data
  const getAvailableRegions = (): string[] => {
    const regionsMap: { [key: string]: boolean } = {};
    mapData.forEach(data => {
      const region = getRegion(data.country);
      regionsMap[region] = true;
    });
    return Object.keys(regionsMap).sort();
  };

  // Select all visible countries
  const selectAllVisibleCountries = () => {
    const visibleCountries = getFilteredCountries();
    const combined = [...selectedCountries, ...visibleCountries];
    const uniqueCountries: string[] = [];
    const seen: { [key: string]: boolean } = {};
    combined.forEach(country => {
      if (!seen[country]) {
        seen[country] = true;
        uniqueCountries.push(country);
      }
    });
    updateTimeSeriesForCountries(uniqueCountries);
    setSelectedCountries(uniqueCountries);
  };

  // Deselect all visible countries
  const deselectAllVisibleCountries = () => {
    const visibleCountries = getFilteredCountries();
    const newSelection = selectedCountries.filter(c => !visibleCountries.includes(c));
    updateTimeSeriesForCountries(newSelection);
    setSelectedCountries(newSelection);
  };

  // Update time series organized by element, then by country
  const updateTimeSeriesForCountries = (countries: string[]) => {
    if (countries.length === 0) {
      setCountryTimeSeries({});
      setCountryStats({});
      setElementTimeSeries({});
      return;
    }

    const aggregationFunc = filters.aggregation || 'average';
    const newElementTimeSeries: { [element: string]: { [country: string]: ChartDataPoint[] } } = {};
    const newCountryStats: { [country: string]: any } = {};

    // Detect field roles
    const areaField = filterFieldConfigs.find(f => f.isAreaField)?.fieldName;
    const yearField = filterFieldConfigs.find(f => f.isYearField)?.fieldName;
    const valueField = filterFieldConfigs.find(f => f.isValueField)?.fieldName;
    const elementFields = filterFieldConfigs.filter(f => 
      !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword'
    );
    const primaryElementField = elementFields.length > 0 ? elementFields[0].fieldName : null;

    // Get selected element values
    const selectedElements = primaryElementField 
      ? (Array.isArray(filters.fields[primaryElementField]) 
          ? filters.fields[primaryElementField] as string[]
          : filters.fields[primaryElementField] ? [filters.fields[primaryElementField] as string] : [])
      : [];
    
    // Get year range from filters
    const fromYear = filters.fromYear ? parseInt(filters.fromYear) : null;
    const toYear = filters.toYear ? parseInt(filters.toYear) : null;

    // Helper to get field value from result
    const getFieldValue = (result: any, fieldName: string): string => {
      return result[fieldName] || result[fieldName.toLowerCase()] || 
             result[fieldName.charAt(0).toUpperCase() + fieldName.slice(1)] || '';
    };

    // Helper function to match country names
    const matchCountry = (area: string, country: string): boolean => {
      const areaNormalized = area.toLowerCase().trim();
      const countryNormalized = country.toLowerCase().trim();
      
      if (areaNormalized === countryNormalized) return true;
      
      const areaParts = areaNormalized.split(',').map(p => p.trim());
      const countryParts = countryNormalized.split(',').map(p => p.trim());
      
      if (areaParts.some(ap => countryParts.some(cp => ap === cp && ap.length > 2))) {
        return true;
      }
      
      if (areaNormalized.includes(countryNormalized) && 
          (areaNormalized === countryNormalized || 
           areaNormalized.startsWith(countryNormalized + ',') ||
           areaNormalized.startsWith(countryNormalized + ' ') ||
           areaNormalized.endsWith(', ' + countryNormalized) ||
           areaNormalized.endsWith(' ' + countryNormalized))) {
        return true;
      }
      
      if (countryNormalized.includes(areaNormalized) && 
          (countryNormalized === areaNormalized || 
           countryNormalized.startsWith(areaNormalized + ',') ||
           countryNormalized.startsWith(areaNormalized + ' ') ||
           countryNormalized.endsWith(', ' + areaNormalized) ||
           countryNormalized.endsWith(' ' + areaNormalized))) {
        return true;
      }
      
      return false;
    };

    // Process each country
    countries.forEach(country => {
      const countryNormalized = country.toLowerCase().trim();
      
      // Filter results for THIS specific country only
      const countryResults = allResults.filter(result => {
        const area = areaField ? getFieldValue(result, areaField) : (result.area || result.Area || 'Unknown');
        return matchCountry(area, country);
      });
      
      // Group by element
      const byElement: { [element: string]: { [year: string]: number[] } } = {};
      
      countryResults.forEach(result => {
        const element = primaryElementField ? getFieldValue(result, primaryElementField) : (result.element || result.Element || '');
        const year = yearField ? getFieldValue(result, yearField) : (result.year || result.Year || '');
        const value = parseFloat(valueField ? getFieldValue(result, valueField) : (result.value || result.Value || '0'));
        
        // Filter by selected elements if specified
        if (selectedElements.length > 0 && !selectedElements.includes(element)) {
          return;
        }
        
        // Check year filter
        if (year) {
          const yearNum = parseInt(year);
          if (fromYear && yearNum < fromYear) return;
          if (toYear && yearNum > toYear) return;
          
          if (!byElement[element]) byElement[element] = {};
          if (!byElement[element][year]) byElement[element][year] = [];
          byElement[element][year].push(value);
        }
      });

      const aggregate = (values: number[]) => {
        if (values.length === 0) return 0;
        switch (aggregationFunc) {
          case 'sum':
            return values.reduce((a, b) => a + b, 0);
          case 'min':
            return Math.min(...values);
          case 'max':
            return Math.max(...values);
          case 'average':
          default:
            return values.reduce((a, b) => a + b, 0) / values.length;
        }
      };

      // Process each element for this country
      Object.keys(byElement).forEach(element => {
        if (!newElementTimeSeries[element]) {
          newElementTimeSeries[element] = {};
        }
        
        const byYear = byElement[element];
        const sortedYears = Object.keys(byYear).sort((a, b) => {
          const yearA = parseInt(a);
          const yearB = parseInt(b);
          return yearA - yearB;
        });

        const timeSeries: ChartDataPoint[] = sortedYears.map(year => ({
          name: year,
          value: aggregate(byYear[year]),
          rawValues: byYear[year],
          count: byYear[year].length,
        }));

        newElementTimeSeries[element][country] = timeSeries;
      });
    });

    setElementTimeSeries(newElementTimeSeries);
    
    // Keep old structure for backward compatibility (for map)
    const newTimeSeries: { [country: string]: ChartDataPoint[] } = {};
    countries.forEach(country => {
      // Get first element's data or combine all elements
      const firstElement = Object.keys(newElementTimeSeries)[0];
      if (firstElement && newElementTimeSeries[firstElement][country]) {
        newTimeSeries[country] = newElementTimeSeries[firstElement][country];
      }
    });
    setCountryTimeSeries(newTimeSeries);
    setCountryStats(newCountryStats);
  };

  // Helper to match country names (improved fuzzy matching)
  const isCountryMatch = (area: string, country: string): boolean => {
    const areaNorm = area.toLowerCase().trim();
    const countryNorm = country.toLowerCase().trim();
    
    // Exact match
    if (areaNorm === countryNorm) return true;
    
    // Partial match
    if (areaNorm.includes(countryNorm) || countryNorm.includes(areaNorm)) return true;
    
    // Remove common suffixes and try again
    const cleanArea = areaNorm.replace(/\s*\(.*?\)\s*/g, '').replace(/,.*$/, '').replace(/\s+and\s+.*$/, '');
    const cleanCountry = countryNorm.replace(/\s*\(.*?\)\s*/g, '').replace(/,.*$/, '').replace(/\s+and\s+.*$/, '');
    if (cleanArea === cleanCountry || cleanArea.includes(cleanCountry) || cleanCountry.includes(cleanArea)) {
      return true;
    }
    
    // Check mapping
    for (const [key, variants] of Object.entries(COUNTRY_NAME_MAPPING)) {
      const keyNorm = key.toLowerCase();
      if (keyNorm === countryNorm || keyNorm === areaNorm) {
        if (variants.some(v => {
          const vNorm = v.toLowerCase();
          return vNorm === areaNorm || vNorm === countryNorm || 
                 areaNorm.includes(vNorm) || countryNorm.includes(vNorm) ||
                 vNorm.includes(areaNorm) || vNorm.includes(countryNorm);
        })) {
          return true;
        }
      }
      // Reverse check
      if (variants.some(v => {
        const vNorm = v.toLowerCase();
        return (vNorm === countryNorm && keyNorm === areaNorm) || 
               (vNorm === areaNorm && keyNorm === countryNorm);
      })) {
        return true;
      }
    }
    
    return false;
  };

  // Create a mapping from country names to values
  const countryValueMap = new Map<string, number>();
  mapData.forEach(data => {
    const normalized = data.country.toLowerCase().trim();
    countryValueMap.set(normalized, data.value);
    
    // Add variations
    if (data.country.includes(',')) {
      countryValueMap.set(data.country.split(',')[0].toLowerCase().trim(), data.value);
    }
    
    // Add mapped variations
    if (COUNTRY_NAME_MAPPING[normalized]) {
      COUNTRY_NAME_MAPPING[normalized].forEach(variant => {
        countryValueMap.set(variant.toLowerCase(), data.value);
      });
    }
  });

  // Function to get country value by name (with fuzzy matching)
  // IMPORTANT: Only returns value if country exists in mapData (current filtered data)
  const getCountryValue = (countryName: string): number | null => {
    if (!countryName || mapData.length === 0) return null;
    
    const normalized = countryName.toLowerCase().trim();
    
    // First, try to find matching country name in mapData
    const matchingCountryName = findMatchingCountryName(countryName);
    if (matchingCountryName) {
      const matchingData = mapData.find(d => 
        d.country.toLowerCase().trim() === matchingCountryName.toLowerCase().trim()
      );
      if (matchingData) {
        return matchingData.value;
      }
      // If matchingCountryName was found but not in mapData, something is wrong
      console.warn(`Matching country "${matchingCountryName}" found but not in mapData`);
    }
    
    // Direct match in mapData (case-insensitive)
    const directMatch = mapData.find(d => 
      d.country.toLowerCase().trim() === normalized
    );
    if (directMatch) return directMatch.value;
    
    // Try without common suffixes and variations - ONLY in mapData
    const variations = [
      normalized.replace(/\s*\(.*?\)\s*/g, ''), // Remove parentheses
      normalized.replace(/,.*$/, ''), // Remove after comma
      normalized.replace(/\s+and\s+.*$/, ''), // Remove "and ..."
      normalized.replace(/\s+republic\s+of\s+/i, ''), // Remove "Republic of"
      normalized.replace(/\s+arab\s+republic\s+of\s+/i, ''), // Remove "Arab Republic of"
      normalized.replace(/\s+arab\s+/i, ''), // Remove "Arab"
      normalized.replace(/^the\s+/i, ''), // Remove "The"
    ];
    
    for (const variation of variations) {
      const trimmed = variation.trim();
      if (trimmed && trimmed !== normalized && trimmed.length >= 3) {
        // Only check in mapData, not in countryValueMap
        const match = mapData.find(d => d.country.toLowerCase().trim() === trimmed);
        if (match) return match.value;
      }
    }
    
    // Also try cross-matching: clean both geoJson name and mapData country names
    for (const data of mapData) {
      const dataCountry = data.country.toLowerCase().trim();
      const dataVariations = [
        dataCountry.replace(/\s*\(.*?\)\s*/g, ''),
        dataCountry.replace(/,.*$/, ''),
        dataCountry.replace(/\s+and\s+.*$/, ''),
        dataCountry.replace(/\s+republic\s+of\s+/i, ''),
        dataCountry.replace(/\s+arab\s+republic\s+of\s+/i, ''),
        dataCountry.replace(/\s+arab\s+/i, ''),
        dataCountry.replace(/^the\s+/i, ''),
      ];
      
      for (const geoVar of variations) {
        const trimmedGeo = geoVar.trim();
        if (trimmedGeo && trimmedGeo.length >= 3) {
          for (const dataVar of dataVariations) {
            const trimmedData = dataVar.trim();
            if (trimmedData && trimmedData.length >= 3 && trimmedGeo === trimmedData) {
              return data.value;
            }
          }
        }
      }
    }
    
    // Try with country name mapping - ONLY in mapData (enhanced)
    for (const [key, variants] of Object.entries(COUNTRY_NAME_MAPPING)) {
      const keyNormalized = key.toLowerCase().trim();
      
      // Check exact match with variants
      if (variants.some(v => v.toLowerCase().trim() === normalized)) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === keyNormalized);
        if (match) return match.value;
      }
      
      // Check if normalized matches key
      if (keyNormalized === normalized) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === keyNormalized);
        if (match) return match.value;
      }
      
      // Check cleaned versions
      const cleanedNormalized = normalized.replace(/\s+arab\s+republic\s+of\s+/i, '').replace(/\s+arab\s+/i, '').trim();
      const normalizedVariants = variants.map(v => v.toLowerCase().trim());
      const normalizedVariantsCleaned = normalizedVariants.map(v => 
        v.replace(/\s+arab\s+republic\s+of\s+/i, '').replace(/\s+arab\s+/i, '').trim()
      );
      
      if (normalizedVariants.includes(cleanedNormalized) || normalizedVariantsCleaned.includes(cleanedNormalized)) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === keyNormalized);
        if (match) return match.value;
      }
    }
    
    // REMOVED: Partial matching is too permissive and causes false positives
    // For example, if mapData only contains "Tunisia", we don't want to match "Canada" to "Tunisia"
    // Only exact matches or very specific variations (handled above) should be used
    
    // Try reverse mapping - ONLY in mapData - STRICT (exact match only)
    for (const [key, variants] of Object.entries(COUNTRY_NAME_MAPPING)) {
      // Only match if normalized exactly matches a variant (not partial)
      if (variants.some(v => {
        const vNorm = v.toLowerCase().trim();
        return vNorm === normalized; // Exact match only
      })) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === key);
        if (match) return match.value;
      }
    }
    
    // Final attempt: use isCountryMatch for fuzzy matching - but VERY strict
    // Only match if names share at least 4 character prefix
    for (const data of mapData) {
      const dataCountry = data.country.toLowerCase().trim();
      if (dataCountry.length >= 4 && normalized.length >= 4) {
        // Check if they share a significant prefix
        const sharedPrefix = Math.min(
          dataCountry.substring(0, 4) === normalized.substring(0, 4) ? 4 : 0,
          dataCountry.substring(0, 5) === normalized.substring(0, 5) ? 5 : 0,
          dataCountry.substring(0, 6) === normalized.substring(0, 6) ? 6 : 0
        );
        
        // Only use isCountryMatch if they share at least 4 character prefix
        if (sharedPrefix >= 4 && isCountryMatch(data.country, countryName)) {
          return data.value;
        }
      }
    }
    
    // Return null if not found in mapData
    return null;
  };

  // Find matching country name from mapData for GeoJSON country name
  const findMatchingCountryName = (geoJsonName: string): string | null => {
    if (!geoJsonName || mapData.length === 0) return null;
    
    const normalized = geoJsonName.toLowerCase().trim();
    
    // Direct match (case-insensitive)
    const directMatch = mapData.find(d => 
      d.country.toLowerCase().trim() === normalized
    );
    if (directMatch) return directMatch.country;
    
    // Try exact match after removing common variations
    const cleanVariations = [
      normalized.replace(/\s*\(.*?\)\s*/g, ''), // Remove parentheses
      normalized.replace(/,.*$/, ''), // Remove after comma
      normalized.replace(/\s+and\s+.*$/, ''), // Remove "and ..."
      normalized.replace(/\s+republic\s+of\s+/i, ''), // Remove "Republic of"
      normalized.replace(/\s+arab\s+republic\s+of\s+/i, ''), // Remove "Arab Republic of"
      normalized.replace(/\s+arab\s+/i, ''), // Remove "Arab"
      normalized.replace(/^the\s+/i, ''), // Remove "The"
    ];
    
    for (const clean of cleanVariations) {
      const trimmed = clean.trim();
      if (trimmed && trimmed !== normalized && trimmed.length >= 3) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === trimmed);
        if (match) {
          console.log(`Match found via variation: "${geoJsonName}" -> "${match.country}" (via "${trimmed}")`);
          return match.country;
        }
      }
    }
    
    // Also try matching the cleaned version against mapData countries
    // For example: "Arab Republic of Egypt" -> "Egypt"
    for (const data of mapData) {
      const dataCountry = data.country.toLowerCase().trim();
      const dataCleanVariations = [
        dataCountry.replace(/\s*\(.*?\)\s*/g, ''),
        dataCountry.replace(/,.*$/, ''),
        dataCountry.replace(/\s+and\s+.*$/, ''),
        dataCountry.replace(/\s+republic\s+of\s+/i, ''),
        dataCountry.replace(/\s+arab\s+republic\s+of\s+/i, ''),
        dataCountry.replace(/\s+arab\s+/i, ''),
        dataCountry.replace(/^the\s+/i, ''),
      ];
      
      for (const cleanGeo of cleanVariations) {
        const trimmedGeo = cleanGeo.trim();
        if (trimmedGeo && trimmedGeo.length >= 3) {
          for (const cleanData of dataCleanVariations) {
            const trimmedData = cleanData.trim();
            if (trimmedData && trimmedData.length >= 3 && trimmedGeo === trimmedData) {
              console.log(`Match found via cross-cleaning: "${geoJsonName}" -> "${data.country}"`);
              return data.country;
            }
          }
        }
      }
    }
    
    // REMOVED: Partial match is too permissive and causes false positives
    // Only exact matches or very specific variations should be used
    
    // Try with country name mapping - enhanced for better matching
    for (const [key, variants] of Object.entries(COUNTRY_NAME_MAPPING)) {
      const keyNormalized = key.toLowerCase().trim();
      
      // Check if geoJsonName matches any variant (exact or after cleaning)
      const normalizedVariants = variants.map(v => v.toLowerCase().trim());
      const normalizedVariantsCleaned = normalizedVariants.map(v => 
        v.replace(/\s+arab\s+republic\s+of\s+/i, '').replace(/\s+arab\s+/i, '').trim()
      );
      
      // Check exact match with variants
      if (normalizedVariants.includes(normalized) || normalizedVariantsCleaned.includes(normalized)) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === keyNormalized);
        if (match) {
          console.log(`Match found via COUNTRY_NAME_MAPPING variant: "${geoJsonName}" -> "${match.country}"`);
          return match.country;
        }
      }
      
      // Check if geoJsonName matches the key itself
      if (keyNormalized === normalized) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === keyNormalized);
        if (match) {
          console.log(`Match found via COUNTRY_NAME_MAPPING key: "${geoJsonName}" -> "${match.country}"`);
          return match.country;
        }
      }
      
      // Check if cleaned geoJsonName matches cleaned variants
      const cleanedNormalized = normalized.replace(/\s+arab\s+republic\s+of\s+/i, '').replace(/\s+arab\s+/i, '').trim();
      if (normalizedVariants.includes(cleanedNormalized) || normalizedVariantsCleaned.includes(cleanedNormalized)) {
        const match = mapData.find(d => d.country.toLowerCase().trim() === keyNormalized);
        if (match) {
          console.log(`Match found via cleaned COUNTRY_NAME_MAPPING: "${geoJsonName}" -> "${match.country}"`);
          return match.country;
        }
      }
      
      // Reverse: check if any variant matches geoJsonName - STRICT (exact match only)
      if (variants.some(v => {
        const vNorm = v.toLowerCase().trim();
        return vNorm === normalized; // Exact match
      })) {
        const match = mapData.find(d => {
          const dataCountry = d.country.toLowerCase().trim();
          return dataCountry === keyNormalized;
        });
        if (match) {
          console.log(`Match found via reverse COUNTRY_NAME_MAPPING: "${geoJsonName}" -> "${match.country}"`);
          return match.country;
        }
      }
    }
    
    // Try compound name matching (e.g., "China, Hong Kong SAR")
    const geoParts = normalized.split(',').map((p: string) => p.trim()).filter((p: string) => p.length >= 3);
    if (geoParts.length > 0) {
      for (const data of mapData) {
        const dataCountry = data.country.toLowerCase().trim();
        const dataParts = dataCountry.split(',').map((p: string) => p.trim()).filter((p: string) => p.length >= 3);
        
        // Check if first part matches exactly (at least 4 characters)
        if (geoParts[0] && dataParts[0] && geoParts[0] === dataParts[0] && geoParts[0].length >= 4) {
          return data.country;
        }
      }
    }
    
    // Try fuzzy matching with isCountryMatch - but only if names share significant prefix
    for (const data of mapData) {
      const dataCountry = data.country.toLowerCase().trim();
      
      // Only use isCountryMatch if names are similar enough (share at least 4 char prefix)
      if (dataCountry.length >= 4 && normalized.length >= 4) {
        const sharedPrefix = Math.min(
          dataCountry.substring(0, 4) === normalized.substring(0, 4) ? 4 : 0,
          dataCountry.substring(0, 5) === normalized.substring(0, 5) ? 5 : 0,
          dataCountry.substring(0, 6) === normalized.substring(0, 6) ? 6 : 0
        );
        
        if (sharedPrefix >= 4 && isCountryMatch(data.country, geoJsonName)) {
          return data.country;
        }
      }
    }
    
    // NO loose matching - return null if no exact or very close match found
    // This prevents false positives like matching "Canada" to "Tunisia"
    
    // DEBUG: Log when no match is found
    console.log(`No match found for "${geoJsonName}". Available countries in mapData:`, 
      mapData.map(d => d.country));
    
    return null;
  };

  // GeoJSON style function
  const getGeoJSONStyle = (feature: any) => {
    const geoJsonCountryName = feature.properties.NAME || feature.properties.name || feature.properties.NAME_EN || feature.properties.ADMIN || '';
    const matchingCountryName = findMatchingCountryName(geoJsonCountryName);
    const value = getCountryValue(geoJsonCountryName);
    const isSelected = selectedCountries.some(c => {
      const countryToCheck = matchingCountryName || geoJsonCountryName;
      return isCountryMatch(c, geoJsonCountryName) || isCountryMatch(c, countryToCheck) ||
             c.toLowerCase().includes(geoJsonCountryName.toLowerCase()) || 
             geoJsonCountryName.toLowerCase().includes(c.toLowerCase());
    });
    
    if (value !== null) {
      const color = getColorForValue(value, maxMapValue);
      return {
        fillColor: color,
        weight: isSelected ? 3.5 : 1.5,
        color: isSelected ? '#1d4ed8' : '#555',
        fillOpacity: isSelected ? 0.95 : 0.7,
        opacity: 1,
        dashArray: isSelected ? '' : '',
      };
    }
    
    // Default style for countries without data
    return {
      fillColor: '#e5e7eb',
      weight: 1,
      color: '#aaa',
      fillOpacity: 0.3,
      opacity: 0.5,
    };
  };

  // Handle GeoJSON feature click
  const onEachCountry = (feature: any, layer: L.Layer) => {
    const geoJsonCountryName = feature.properties.NAME || feature.properties.name || feature.properties.NAME_EN || feature.properties.ADMIN || '';
    const matchingCountryName = findMatchingCountryName(geoJsonCountryName);
    // Use matchingCountryName if found, otherwise try geoJsonCountryName, or look in mapData
    const countryNameForData = matchingCountryName || 
                                mapData.find(d => isCountryMatch(d.country, geoJsonCountryName))?.country ||
                                geoJsonCountryName;
    
    // Get value from mapData directly (most reliable)
    let value: number | null = null;
    if (matchingCountryName) {
      const mapDataEntry = mapData.find(d => 
        d.country.toLowerCase().trim() === matchingCountryName.toLowerCase().trim()
      );
      if (mapDataEntry) {
        value = mapDataEntry.value;
      } else {
        // Debug: log if matchingCountryName doesn't match any entry in mapData
        console.warn(`Matching country name "${matchingCountryName}" not found in mapData. Available countries:`, 
          mapData.map(d => d.country));
      }
    }
    
    // Fallback to getCountryValue if not found in mapData
    if (value === null) {
      value = getCountryValue(geoJsonCountryName);
      // Debug: log if we're using fallback
      if (value !== null) {
        console.log(`Using fallback getCountryValue for "${geoJsonCountryName}": ${value}`);
      }
    }
    
    // Debug: log the final value and matching info
    if (value !== null) {
      console.log(`Country: ${geoJsonCountryName}, Matching: ${matchingCountryName || 'none'}, Value: ${value}, MapData countries:`, 
        mapData.map(d => `${d.country}: ${d.value}`));
    }
    // Try to get stats with multiple name variations
    const stats = (matchingCountryName && countryStats[matchingCountryName]) || 
                  countryStats[countryNameForData] ||
                  (Object.keys(countryStats).find(key => 
                    isCountryMatch(key, geoJsonCountryName) || 
                    (matchingCountryName && isCountryMatch(key, matchingCountryName)) ||
                    isCountryMatch(geoJsonCountryName, key)
                  ) ? countryStats[Object.keys(countryStats).find(key => 
                    isCountryMatch(key, geoJsonCountryName) || 
                    (matchingCountryName && isCountryMatch(key, matchingCountryName)) ||
                    isCountryMatch(geoJsonCountryName, key)
                  )!] : null) ||
                  null;
    const isSelected = selectedCountries.some(c => 
      isCountryMatch(c, geoJsonCountryName) || 
      (matchingCountryName && isCountryMatch(c, matchingCountryName)) ||
      c.toLowerCase().includes(geoJsonCountryName.toLowerCase()) || 
      geoJsonCountryName.toLowerCase().includes(c.toLowerCase())
    );
    
    if (value !== null) {
      layer.on({
        click: () => {
          // Use the matching country name from mapData if found
          const countryToSelect = matchingCountryName || geoJsonCountryName;
          handleCountryClick(countryToSelect);
        },
        mouseover: (e: L.LeafletMouseEvent) => {
          const layer = e.target;
          layer.setStyle({
            weight: 4,
            color: '#3b82f6',
            fillOpacity: 0.95,
          });
        },
        mouseout: (e: L.LeafletMouseEvent) => {
          const layer = e.target;
          const isSelectedNow = selectedCountries.some(c => 
            isCountryMatch(c, geoJsonCountryName) || 
            (matchingCountryName && isCountryMatch(c, matchingCountryName)) ||
            c.toLowerCase().includes(geoJsonCountryName.toLowerCase()) || 
            geoJsonCountryName.toLowerCase().includes(c.toLowerCase())
          );
          layer.setStyle({
            weight: isSelectedNow ? 3.5 : 1.5,
            color: isSelectedNow ? '#1d4ed8' : '#555',
            fillOpacity: isSelectedNow ? 0.95 : 0.7,
          });
        },
      });
      
      // Calculate stats on the fly if not available - STRICT matching per country
      const currentStats = stats || (() => {
        // IMPORTANT: Only calculate stats if this country has data in mapData
        // This prevents showing stats for countries that don't have data
        const hasDataInMap = mapData.some(d => {
          const dataCountry = d.country.toLowerCase().trim();
          const geoNorm = geoJsonCountryName.toLowerCase().trim();
          const matchNorm = matchingCountryName ? matchingCountryName.toLowerCase().trim() : '';
          return dataCountry === geoNorm || 
                 dataCountry === matchNorm ||
                 (matchingCountryName && dataCountry === matchingCountryName.toLowerCase().trim());
        });
        
        if (!hasDataInMap) {
          console.log(`No data in mapData for "${geoJsonCountryName}". Available:`, mapData.map(d => d.country));
          return null; // Don't show stats if country is not in mapData
        }
        
        const byYear: { [year: string]: number[] } = {};
        const allVals: number[] = [];
        
        // Get the country name to match - use the one from mapData if available
        const countryToMatch = matchingCountryName || countryNameForData || geoJsonCountryName;
        if (!countryToMatch) return null;
        
        // Get selected elements dynamically
        const _areaField = filterFieldConfigs.find(f => f.isAreaField)?.fieldName;
        const _yearField = filterFieldConfigs.find(f => f.isYearField)?.fieldName;
        const _valueField = filterFieldConfigs.find(f => f.isValueField)?.fieldName;
        const _elementFields = filterFieldConfigs.filter(f => 
          !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword'
        );
        const _primaryElementField = _elementFields.length > 0 ? _elementFields[0].fieldName : null;
        const selectedElements = _primaryElementField 
          ? (Array.isArray(filters.fields[_primaryElementField]) 
              ? filters.fields[_primaryElementField] as string[]
              : filters.fields[_primaryElementField] ? [filters.fields[_primaryElementField] as string] : [])
          : [];
        
        // Helper to get field value from result
        const getFieldValue = (result: any, fieldName: string): string => {
          return result[fieldName] || result[fieldName.toLowerCase()] || 
                 result[fieldName.charAt(0).toUpperCase() + fieldName.slice(1)] || '';
        };

        // Filter results for THIS specific country only - very strict matching
        const countryResults = allResults.filter(result => {
          const area = _areaField ? getFieldValue(result, _areaField) : (result.area || result.Area || 'Unknown');
          const element = _primaryElementField ? getFieldValue(result, _primaryElementField) : (result.element || result.Element || '');
          
          // Filter by element if specified
          if (selectedElements.length > 0 && !selectedElements.includes(element)) {
            return false;
          }
          
          // STRICT country matching - must match exactly or be very close
          const areaNormalized = area.toLowerCase().trim();
          const countryNormalized = countryToMatch.toLowerCase().trim();
          
          // 1. Exact match
          if (areaNormalized === countryNormalized) {
            return true;
          }
          
          // 2. Check if area starts with country or vice versa (for cases like "China, Hong Kong SAR")
          const areaParts = areaNormalized.split(',').map((p: string) => p.trim());
          const countryParts = countryNormalized.split(',').map((p: string) => p.trim());
          
          // If any part of area matches any part of country exactly
          if (areaParts.some((ap: string) => countryParts.some((cp: string) => ap === cp && ap.length > 2))) {
            return true;
          }
          
          // 3. Check if one contains the other as a complete phrase
          if (areaNormalized.includes(countryNormalized) && 
              (areaNormalized === countryNormalized || 
               areaNormalized.startsWith(countryNormalized + ',') ||
               areaNormalized.startsWith(countryNormalized + ' ') ||
               areaNormalized.endsWith(', ' + countryNormalized) ||
               areaNormalized.endsWith(' ' + countryNormalized))) {
            return true;
          }
          
          if (countryNormalized.includes(areaNormalized) && 
              (countryNormalized === areaNormalized || 
               countryNormalized.startsWith(areaNormalized + ',') ||
               countryNormalized.startsWith(areaNormalized + ' ') ||
               countryNormalized.endsWith(', ' + areaNormalized) ||
               countryNormalized.endsWith(' ' + areaNormalized))) {
            return true;
          }
          
          return false;
        });
        
        // Now process only the filtered results for this country
        countryResults.forEach(result => {
          const year = _yearField ? getFieldValue(result, _yearField) : (result.year || result.Year || '');
          const val = parseFloat(_valueField ? getFieldValue(result, _valueField) : (result.value || result.Value || '0'));
          
          // Check year filter
          if (year) {
            const yearNum = parseInt(year);
            const fromYear = filters.fromYear ? parseInt(filters.fromYear) : null;
            const toYear = filters.toYear ? parseInt(filters.toYear) : null;
            
            if (fromYear && yearNum < fromYear) return;
            if (toYear && yearNum > toYear) return;
            
            if (!byYear[year]) byYear[year] = [];
            byYear[year].push(val);
            allVals.push(val);
          }
        });
        
        if (allVals.length === 0) return null;
        
        const aggFunc = filters.aggregation || 'average';
        const aggregate = (vals: number[]) => {
          if (vals.length === 0) return 0;
          switch (aggFunc) {
            case 'sum': return vals.reduce((a, b) => a + b, 0);
            case 'min': return Math.min(...vals);
            case 'max': return Math.max(...vals);
            case 'average':
            default: return vals.reduce((a, b) => a + b, 0) / vals.length;
          }
        };
        
        const sorted = [...allVals].sort((a, b) => a - b);
        const timeSeries = Object.keys(byYear).sort().map(y => aggregate(byYear[y]));
        
        return {
          total: allVals.reduce((a, b) => a + b, 0),
          average: aggregate(allVals),
          min: Math.min(...allVals),
          max: Math.max(...allVals),
          median: sorted[Math.floor(sorted.length / 2)],
          count: allVals.length,
          latestValue: timeSeries.length > 0 ? timeSeries[timeSeries.length - 1] : 0,
          firstValue: timeSeries.length > 0 ? timeSeries[0] : 0,
          trend: timeSeries.length > 1 ? 
            ((timeSeries[timeSeries.length - 1] - timeSeries[0]) / timeSeries[0] * 100) : 0,
        };
      })();
      
      // Enhanced popup with more information - Improved Design
      const displayName = matchingCountryName !== geoJsonCountryName ? 
        `${geoJsonCountryName}<br/><span style="font-size: 11px; color: #6b7280; font-weight: normal;">(Data: ${matchingCountryName})</span>` : 
        geoJsonCountryName;
      
      // Get selected elements for display
      const _elemFields = filterFieldConfigs.filter(f => 
        !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword'
      );
      const _primElemField = _elemFields.length > 0 ? _elemFields[0].fieldName : null;
      const selectedElements = _primElemField 
        ? (Array.isArray(filters.fields[_primElemField]) 
            ? filters.fields[_primElemField] as string[]
            : filters.fields[_primElemField] ? [filters.fields[_primElemField] as string] : [])
        : [];
      const elementsDisplay = selectedElements.length > 0 ? selectedElements.join(', ') : 'All';
      
      // Get dynamic labels
      const valueFieldLabel = filterFieldConfigs.find(f => f.isValueField)?.displayName || 'Value';
      const itemFieldLabel = filterFieldConfigs.find(f => !f.isAreaField && !f.isYearField && !f.isValueField)?.displayName || 'Item';
      const itemFieldValue = Object.entries(filters.fields)
        .filter(([k]) => !filterFieldConfigs.find(f => f.isAreaField && f.fieldName === k) && 
                          !filterFieldConfigs.find(f => f.isYearField && f.fieldName === k))
        .map(([, v]) => Array.isArray(v) ? v.join(', ') : v)
        .filter(Boolean)
        .join(' - ') || itemFieldLabel;
      const unitLabel = filterFieldConfigs.find(f => f.isValueField)?.displayName?.includes('(') 
        ? filterFieldConfigs.find(f => f.isValueField)!.displayName.match(/\(([^)]+)\)/)?.[1] || '' 
        : '';
      
      // Get year range
      const yearRange = filters.fromYear && filters.toYear 
        ? `${filters.fromYear} - ${filters.toYear}`
        : filters.fromYear 
        ? `From ${filters.fromYear}`
        : filters.toYear 
        ? `Until ${filters.toYear}`
        : 'All Years';
      
      const popupContent = `
        <div style="min-width: 280px; max-width: 320px; text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <!-- Header -->
          <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; padding: 12px; border-radius: 6px 6px 0 0; margin: -10px -10px 10px -10px;">
            <div style="font-weight: bold; font-size: 17px; margin-bottom: 4px;">
              ${displayName}
            </div>
            ${isSelected ? '<div style="font-size: 12px; background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; display: inline-block; margin-top: 4px;">✓ Selected</div>' : ''}
          </div>
          
          <!-- Main Value Card -->
          <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 12px; border-radius: 6px; margin-bottom: 10px; border: 1px solid #e2e8f0;">
            <div style="font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; font-weight: 600;">
              ${itemFieldValue}
            </div>
            <div style="font-size: 24px; font-weight: bold; color: #1e40af; margin-bottom: 2px;">
              ${value.toLocaleString(undefined, {maximumFractionDigits: 0})}${unitLabel ? ` <span style="font-size: 14px; color: #64748b;">${unitLabel}</span>` : ''}
            </div>
            <div style="font-size: 11px; color: #64748b; display: flex; align-items: center; gap: 6px; margin-top: 4px;">
              <span style="background: #3b82f6; color: white; padding: 2px 6px; border-radius: 4px; font-weight: 600;">
                ${(filters.aggregation || 'average').charAt(0).toUpperCase() + (filters.aggregation || 'average').slice(1)}
              </span>
              <span>•</span>
              <span>${yearRange}</span>
            </div>
          </div>
          
          <!-- Elements Info -->
          <div style="background: #f8fafc; padding: 8px; border-radius: 4px; margin-bottom: 10px; border-left: 3px solid #3b82f6;">
            <div style="font-size: 10px; color: #64748b; text-transform: uppercase; margin-bottom: 2px; font-weight: 600;">${_primElemField ? filterFieldConfigs.find(f => f.fieldName === _primElemField)?.displayName || _primElemField : 'Element(s)'}</div>
            <div style="font-size: 12px; color: #1e293b; font-weight: 500;">${elementsDisplay}</div>
          </div>
          
          ${currentStats ? `
            <!-- Statistics Grid -->
            <div style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e2e8f0; margin-bottom: 10px;">
              <div style="font-size: 11px; color: #475569; margin-bottom: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">
                📊 Statistics
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 11px;">
                <div style="padding: 6px; background: #f1f5f9; border-radius: 4px;">
                  <div style="color: #64748b; margin-bottom: 2px;">Average</div>
                  <div style="font-weight: bold; color: #1e293b;">${currentStats.average.toLocaleString(undefined, {maximumFractionDigits: 1})}${unitLabel ? ' ' + unitLabel : ''}</div>
                </div>
                <div style="padding: 6px; background: #f1f5f9; border-radius: 4px;">
                  <div style="color: #64748b; margin-bottom: 2px;">Total</div>
                  <div style="font-weight: bold; color: #1e293b;">${currentStats.total.toLocaleString(undefined, {maximumFractionDigits: 0})}${unitLabel ? ' ' + unitLabel : ''}</div>
                </div>
                <div style="padding: 6px; background: #f1f5f9; border-radius: 4px;">
                  <div style="color: #64748b; margin-bottom: 2px;">Min</div>
                  <div style="font-weight: bold; color: #1e293b;">${currentStats.min.toLocaleString()}${unitLabel ? ' ' + unitLabel : ''}</div>
                </div>
                <div style="padding: 6px; background: #f1f5f9; border-radius: 4px;">
                  <div style="color: #64748b; margin-bottom: 2px;">Max</div>
                  <div style="font-weight: bold; color: #1e293b;">${currentStats.max.toLocaleString()}${unitLabel ? ' ' + unitLabel : ''}</div>
                </div>
                ${currentStats.trend !== 0 ? `
                  <div style="grid-column: 1 / -1; padding: 6px; background: ${currentStats.trend > 0 ? '#dcfce7' : '#fee2e2'}; border-radius: 4px; margin-top: 4px;">
                    <div style="color: #64748b; margin-bottom: 2px;">Trend</div>
                    <div style="font-weight: bold; color: ${currentStats.trend > 0 ? '#059669' : '#dc2626'}; font-size: 12px;">
                      ${currentStats.trend > 0 ? '↑' : '↓'} ${Math.abs(currentStats.trend).toFixed(1)}%
                    </div>
                  </div>
                ` : ''}
              </div>
            </div>
          ` : ''}
          
          <!-- Action Footer -->
          <div style="background: #f8fafc; padding: 10px; border-radius: 6px; border: 1px solid #e2e8f0; text-align: center;">
            <div style="font-size: 11px; color: #64748b; margin-bottom: 4px;">
              ${isSelected ? '✓ Selected for comparison' : 'Click to select for comparison'}
            </div>
            ${selectedCountries.length > 0 ? `
              <div style="font-size: 10px; color: #3b82f6; font-weight: 600; padding: 4px; background: white; border-radius: 4px; display: inline-block;">
                ${selectedCountries.length} country${selectedCountries.length > 1 ? 'ies' : ''} selected
              </div>
            ` : ''}
          </div>
        </div>
      `;
      
      layer.bindPopup(popupContent);
    } else {
      // Enhanced popup for countries without data
      layer.bindPopup(`
        <div style="min-width: 200px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
          <div style="background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%); color: white; padding: 12px; border-radius: 6px 6px 0 0; margin: -10px -10px 10px -10px;">
            <div style="font-weight: bold; font-size: 16px; margin-bottom: 4px;">${geoJsonCountryName}</div>
          </div>
          <div style="padding: 16px;">
            <div style="font-size: 48px; margin-bottom: 8px;">📭</div>
            <div style="color: #64748b; font-size: 13px; font-weight: 500;">No data available</div>
            <div style="color: #94a3b8; font-size: 11px; margin-top: 4px;">for selected filters</div>
          </div>
        </div>
      `);
    }
  };

  // Compute dynamic labels at component level for use in expanded modals
  const _areaFieldConfig = filterFieldConfigs.find(f => f.isAreaField);
  const _yearFieldConfig = filterFieldConfigs.find(f => f.isYearField);
  const _valueFieldConfig = filterFieldConfigs.find(f => f.isValueField);
  const _primaryElementField = filterFieldConfigs.find(f => !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword' && !f.isItemLike);
  const _primaryItemField = filterFieldConfigs.find(f => f.isItemLike);
  const _elementFieldName = _primaryElementField?.fieldName || '';
  const _itemFieldName = _primaryItemField?.fieldName || '';
  
  const _getFieldValue = (result: any, fieldName: string): any => {
    if (!fieldName) return '';
    if (result[fieldName] !== undefined) return result[fieldName];
    const lower = fieldName.toLowerCase();
    if (result[lower] !== undefined) return result[lower];
    const cap = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    if (result[cap] !== undefined) return result[cap];
    return '';
  };

  const _selectedElements = (() => {
    if (_elementFieldName) {
      const val = filters.fields[_elementFieldName];
      return Array.isArray(val) ? val : val ? [val as string] : [];
    }
    return [];
  })();

  const _primaryElement = _selectedElements.length > 0 ? _selectedElements[0] : (_primaryElementField?.displayName || 'Value');
  const _itemFieldValue = _itemFieldName ? (filters.fields[_itemFieldName] as string || '') : '';
  
  const _unitLabel = (() => {
    const dataSource = globalResults.length > 0 ? globalResults : allResults;
    if (dataSource.length > 0) {
      const unit = _getFieldValue(dataSource[0], 'Unit') || _getFieldValue(dataSource[0], 'unit');
      if (unit) return unit;
    }
    return _valueFieldConfig?.displayName || 'Value';
  })();
  const _unitShort = _unitLabel.length > 3 ? _unitLabel.substring(0, 3) : _unitLabel;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar - Fixed Left */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-y-auto">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h1 className="text-xl font-bold text-gray-900">{selectedIndexMeta?.title || selectedIndex || 'Data Visualization'}</h1>
          <p className="text-xs text-gray-600 mt-1">{selectedIndexMeta?.description || 'Visualize data across available indices'}</p>
        </div>

        {/* Filters - Scrollable */}
        <div className="flex-1 p-4 space-y-4">
          {/* Index Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Database className="h-4 w-4 inline mr-1" />
              Data Index
            </label>
            <select
              value={selectedIndex}
              onChange={(e) => {
                const newIndex = e.target.value;
                setSelectedIndex(newIndex);
                // Reset filters and data when index changes
                setFilterFieldConfigs([]);
                setFilterValues({});
                setSelectedIndexMeta(null);
                setFilters({ index: newIndex, fields: {}, aggregation: 'average' });
                setMapData([]);
                setSelectedCountries([]);
                setCountryTimeSeries({});
                setElementTimeSeries({});
                setAllResults([]);
                setGlobalResults([]);
                setCountryStats({});
                setError(null);
                setInitialLoading(true);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
              <option value="">Select an index...</option>
              {indices
                .filter(i => i.status === 'available')
                .map(index => (
                  <option key={index.name} value={index.name}>
                    {index.displayName || index.name}
                    {index.hasMetadata && index.filterFields?.length > 0 ? ' ✓' : ''}
                  </option>
                ))}
            </select>
            {selectedIndex && selectedIndexMeta && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedIndexMeta.availableFilterFields?.length || 0} available fields
                {selectedIndexMeta.filterFields?.length > 0 && ` · ${selectedIndexMeta.filterFields.length} configured`}
              </p>
            )}
            {selectedIndex && indices.find(i => i.name === selectedIndex)?.documentCount === 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-700 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>This index has no data. Import documents to start visualizing.</span>
              </div>
            )}
          </div>

          {/* Dynamic Filter Fields from Metadata */}
          <div className="space-y-4">
          {filterFieldConfigs
            .filter(f => !f.isYearField && !f.isValueField)
            .sort((a, b) => a.priority - b.priority)
            .map(fieldConfig => {
              const fieldValues = filterValues[fieldConfig.fieldName] || filterValues[fieldConfig.fieldName.toLowerCase()] || [];
              const currentValue = filters.fields[fieldConfig.fieldName];
              const isMultiSelect = fieldConfig.multiSelect;
              const isArea = fieldConfig.isAreaField;
              const currentArray: string[] = Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue as string] : [];
              const selectedCount = currentArray.length;

              return (
                <div key={fieldConfig.fieldName}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isArea ? <Globe className="h-4 w-4 inline mr-1" /> : <Database className="h-4 w-4 inline mr-1" />}
                    {fieldConfig.displayName}
                    {fieldConfig.required && <span className="text-red-500"> *</span>}
                    {isMultiSelect && selectedCount > 0 && (
                      <span className="text-xs text-indigo-600 font-medium"> ({selectedCount} selected)</span>
                    )}
                  </label>

                  {fieldValues.length > 0 ? (
                    <div className="space-y-2">
                      {/* Selected Badges for multi-select */}
                      {isMultiSelect && selectedCount > 0 && (
                        <div className="flex flex-wrap gap-2 p-2 bg-indigo-50 rounded-md border border-indigo-200 min-h-[40px]">
                          {currentArray.map((val) => (
                            <div
                              key={val}
                              className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded-md text-xs font-medium"
                            >
                              <span>{val}</span>
                              <button
                                onClick={() => {
                                  const newVals = currentArray.filter(v => v !== val);
                                  if (isArea) {
                                    handleAreaChange(newVals);
                                  } else {
                                    handleFilterChange(fieldConfig.fieldName, newVals);
                                  }
                                }}
                                className="ml-1 hover:bg-indigo-700 rounded-full p-0.5 transition-colors"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              if (isArea) {
                                handleAreaChange([]);
                              } else {
                                handleFilterChange(fieldConfig.fieldName, []);
                              }
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium self-center"
                          >
                            Clear All
                          </button>
                        </div>
                      )}

                      {/* Single-select: dropdown */}
                      {!isMultiSelect && (
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                          <select
                            value={(currentValue as string) || ''}
                            onChange={(e) => handleFilterChange(fieldConfig.fieldName, e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm appearance-none bg-white"
                          >
                            <option value="">Select {fieldConfig.displayName}...</option>
                            <option value="__all__">All {fieldConfig.displayName}</option>
                            {fieldValues.map((fv) => (
                              <option key={fv.value} value={fv.value}>
                                {fv.value} {fv.count ? `(${fv.count})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Multi-select: search + checkbox list */}
                      {isMultiSelect && (
                        <>
                          {/* Search Input */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              value={isArea ? countrySearchTerm : elementSearchTerm}
                              onChange={(e) => {
                                if (isArea) setCountrySearchTerm(e.target.value);
                                else setElementSearchTerm(e.target.value);
                              }}
                              placeholder={`Search ${fieldConfig.displayName.toLowerCase()}...`}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            />
                          </div>

                          {/* Checkbox List */}
                          <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-sm">
                            <div className="p-2 space-y-1">
                              {(() => {
                                const searchTerm = isArea ? countrySearchTerm : elementSearchTerm;
                                const filtered = fieldValues.filter((fv: FilterValue) => {
                                  if (!searchTerm.trim()) return true;
                                  return fv.value && fv.value.toLowerCase().includes(searchTerm.toLowerCase());
                                });

                                if (filtered.length === 0) {
                                  return (
                                    <div className="text-center text-sm text-gray-500 py-4">
                                      {fieldValues.length === 0 
                                        ? `No ${fieldConfig.displayName.toLowerCase()} available. Loading...`
                                        : `No ${fieldConfig.displayName.toLowerCase()} found matching your search.`
                                      }
                                    </div>
                                  );
                                }

                                return filtered.slice(0, isArea ? 200 : 100).map((fv: FilterValue) => {
                                  const isSelected = currentArray.includes(fv.value);
                                  return (
                                    <div
                                      key={fv.value}
                                      onClick={() => {
                                        if (isSelected) {
                                          const newVals = currentArray.filter(v => v !== fv.value);
                                          if (isArea) handleAreaChange(newVals);
                                          else handleFilterChange(fieldConfig.fieldName, newVals);
                                        } else {
                                          const newVals = [...currentArray, fv.value];
                                          if (isArea) handleAreaChange(newVals);
                                          else handleFilterChange(fieldConfig.fieldName, newVals);
                                        }
                                      }}
                                      className={`flex items-center gap-2 p-2.5 rounded-md cursor-pointer transition-all ${
                                        isSelected
                                          ? 'bg-indigo-50 border-2 border-indigo-400 shadow-sm'
                                          : 'hover:bg-gray-50 border-2 border-transparent hover:border-gray-300'
                                      }`}
                                    >
                                      {isSelected ? (
                                        <CheckSquare className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                                      ) : (
                                        <Square className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                      )}
                                      <span className={`text-sm flex-1 ${isSelected ? 'text-indigo-900 font-semibold' : 'text-gray-700'}`}>
                                        {fv.value}
                                      </span>
                                      {fv.count && (
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${isSelected ? 'bg-indigo-200 text-indigo-800' : 'bg-gray-200 text-gray-600'}`}>
                                          {fv.count}
                                        </span>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    /* Fallback: text input when no filter values available */
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={Array.isArray(currentValue) ? currentValue.join(', ') : (currentValue || '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (isMultiSelect && val.includes(',')) {
                            const vals = val.split(',').map(v => v.trim()).filter(v => v);
                            handleFilterChange(fieldConfig.fieldName, vals);
                          } else {
                            handleFilterChange(fieldConfig.fieldName, val);
                          }
                        }}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        placeholder={`Enter ${fieldConfig.displayName}${isMultiSelect ? ' (comma-separated)' : ''}`}
                      />
                    </div>
                  )}

                  {/* Show selected value for single-select */}
                  {!isMultiSelect && currentValue && (
                    <div className="mt-1 text-xs text-gray-500">
                      Selected: <span className="font-medium text-indigo-600">{currentValue as string}</span>
                    </div>
                  )}
                </div>
              );
            })}

          {/* No filter fields configured - show message */}
          {filterFieldConfigs.filter(f => !f.isYearField && !f.isValueField).length === 0 && (
            <div className="text-center text-sm text-gray-500 py-4 bg-gray-50 rounded-md border border-gray-200">
              <Database className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p>No filter fields configured for this index.</p>
              <p className="text-xs mt-1">Select an index with metadata or auto-configure it.</p>
            </div>
          )}

          {/* Advanced Filters Toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-600 hover:text-gray-800 py-2 transition-colors"
            >
              <span className="flex items-center gap-1">
                <Filter className="h-4 w-4" />
                Advanced Filters
              </span>
              {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {showAdvanced && (
              <div className="space-y-4 mt-3 pl-1">
                {/* From Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    From Year
                  </label>
                  <input
                    type="number"
                    value={filters.fromYear || ''}
                    onChange={(e) => handleFilterChange('fromYear', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="e.g., 2014"
                  />
                </div>

                {/* To Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    To Year
                  </label>
                  <input
                    type="number"
                    value={filters.toYear || ''}
                    onChange={(e) => handleFilterChange('toYear', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="e.g., 2019"
                  />
                </div>

                {/* Aggregation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aggregation
                  </label>
                  <select
                    value={filters.aggregation || 'average'}
                    onChange={(e) => handleFilterChange('aggregation', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  >
                    <option value="average">Average</option>
                    <option value="sum">Sum</option>
                    <option value="min">Minimum</option>
                    <option value="max">Maximum</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          </div>

          {/* Visualize Button */}
          <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200">
            <button
              onClick={handleVisualize}
              disabled={loading || !selectedIndex}
              className="w-full bg-indigo-600 text-white px-6 py-2.5 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center font-medium"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Visualize'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span>{error}</span>
                </div>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

      {/* Visualizations - Compact & Innovative Design */}
      {mapData.length > 0 && (() => {
        // Dynamic field detection from filterFieldConfigs
        const areaFieldConfig = filterFieldConfigs.find(f => f.isAreaField);
        const yearFieldConfig = filterFieldConfigs.find(f => f.isYearField);
        const valueFieldConfig = filterFieldConfigs.find(f => f.isValueField);
        const primaryElementField = filterFieldConfigs.find(f => !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword' && !f.isItemLike);
        const primaryItemField = filterFieldConfigs.find(f => f.isItemLike);
        
        const areaFieldName = areaFieldConfig?.fieldName || 'Area';
        const yearFieldName = yearFieldConfig?.fieldName || 'Year';
        const valueFieldName = valueFieldConfig?.fieldName || 'Value';
        const elementFieldName = primaryElementField?.fieldName || '';
        const itemFieldName = primaryItemField?.fieldName || '';
        
        // Helper to get field value from result trying original, lowercase, capitalized
        const getFieldValue = (result: any, fieldName: string): any => {
          if (!fieldName) return '';
          if (result[fieldName] !== undefined) return result[fieldName];
          const lower = fieldName.toLowerCase();
          if (result[lower] !== undefined) return result[lower];
          const cap = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
          if (result[cap] !== undefined) return result[cap];
          return '';
        };

        // Derive unit label from data or config
        const unitLabel = (() => {
          const dataSource = globalResults.length > 0 ? globalResults : allResults;
          if (dataSource.length > 0) {
            const unit = getFieldValue(dataSource[0], 'Unit') || getFieldValue(dataSource[0], 'unit');
            if (unit) return unit;
          }
          return valueFieldConfig?.displayName || 'Value';
        })();
        const unitShort = unitLabel.length > 3 ? unitLabel.substring(0, 3) : unitLabel;

        // Calculate global statistics once
        const dataSource = globalResults.length > 0 ? globalResults : allResults;
        const selectedElements = (() => {
          if (elementFieldName) {
            const val = filters.fields[elementFieldName];
            return Array.isArray(val) ? val : val ? [val as string] : [];
          }
          return [];
        })();
        const fromYear = filters.fromYear ? parseInt(filters.fromYear) : null;
        const toYear = filters.toYear ? parseInt(filters.toYear) : null;
        const aggregationFunc = filters.aggregation || 'average';
        
        const globalData = dataSource.filter(result => {
          if (elementFieldName && selectedElements.length > 0) {
            const element = getFieldValue(result, elementFieldName) as string;
            if (!selectedElements.includes(element)) return false;
          }
          const year = getFieldValue(result, yearFieldName) as string;
          if (year) {
            const yearNum = parseInt(year);
            if (fromYear && yearNum < fromYear) return false;
            if (toYear && yearNum > toYear) return false;
          }
          return true;
        });
        
        const byCountry: { [key: string]: number[] } = {};
        globalData.forEach(result => {
          const area = (getFieldValue(result, areaFieldName) || 'Unknown') as string;
          const value = parseFloat((getFieldValue(result, valueFieldName) || '0') as string);
          if (area && area !== 'Unknown' && !isNaN(value) && value > 0) {
            if (!byCountry[area]) byCountry[area] = [];
            byCountry[area].push(value);
          }
        });
        
        const aggregate = (values: number[]) => {
          if (values.length === 0) return 0;
          switch (aggregationFunc) {
            case 'sum': return values.reduce((a, b) => a + b, 0);
            case 'min': return Math.min(...values);
            case 'max': return Math.max(...values);
            case 'average':
            default: return values.reduce((a, b) => a + b, 0) / values.length;
          }
        };
        
        const topProducers = Object.keys(byCountry)
          .map(country => ({ country, value: aggregate(byCountry[country]) }))
          .filter(d => d.value > 0)
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);
        
        const totalWorldProduction = Object.keys(byCountry).reduce((sum, country) => sum + aggregate(byCountry[country]), 0);
        const topProducer = topProducers[0];
        const primaryElement = selectedElements.length > 0 ? selectedElements[0] : (primaryElementField?.displayName || 'Value');
        const yearRange = filters.fromYear && filters.toYear ? `${filters.fromYear} - ${filters.toYear}` : filters.fromYear ? `From ${filters.fromYear}` : filters.toYear ? `Until ${filters.toYear}` : 'All Years';
        const yearsCount = fromYear && toYear ? toYear - fromYear + 1 : (allResults.length > 0 ? new Set(allResults.map(r => getFieldValue(r, yearFieldName))).size : 0);
        // Calculate real trend from historical data
        const trend = (() => {
          if (globalData.length === 0) return 0;
          // Group by year and calculate total per year
          const byYear: { [year: string]: number[] } = {};
          globalData.forEach(result => {
            const year = getFieldValue(result, yearFieldName) as string;
            const value = parseFloat((getFieldValue(result, valueFieldName) || '0') as string);
            if (year && !isNaN(value) && value > 0) {
              if (!byYear[year]) byYear[year] = [];
              byYear[year].push(value);
            }
          });
          const years = Object.keys(byYear).sort((a, b) => parseInt(a) - parseInt(b));
          if (years.length < 2) return 0;
          const firstYearValue = aggregate(byYear[years[0]]);
          const lastYearValue = aggregate(byYear[years[years.length - 1]]);
          if (firstYearValue === 0) return 0;
          return ((lastYearValue - firstYearValue) / firstYearValue) * 100;
        })();
        
        // Calculate per-country trends for insight cards
        const countryTrends: { country: string; trend: number; latestValue: number; avgValue: number; rank: number }[] = (() => {
          const trends: { country: string; trend: number; latestValue: number; avgValue: number; rank: number }[] = [];
          const sorted = Object.keys(byCountry).sort((a, b) => aggregate(byCountry[b]) - aggregate(byCountry[a]));
          sorted.forEach((country, idx) => {
            const values = byCountry[country];
            if (values.length === 0) return;
            // Get year-by-year data for this country
            const countryByYear: { [year: string]: number[] } = {};
            globalData.forEach(result => {
              const area = (getFieldValue(result, areaFieldName) || 'Unknown') as string;
              if (area !== country) return;
              const year = getFieldValue(result, yearFieldName) as string;
              const value = parseFloat((getFieldValue(result, valueFieldName) || '0') as string);
              if (year && !isNaN(value) && value > 0) {
                if (!countryByYear[year]) countryByYear[year] = [];
                countryByYear[year].push(value);
              }
            });
            const years = Object.keys(countryByYear).sort((a, b) => parseInt(a) - parseInt(b));
            let trend = 0;
            if (years.length >= 2) {
              const firstVal = aggregate(countryByYear[years[0]]);
              const lastVal = aggregate(countryByYear[years[years.length - 1]]);
              trend = firstVal === 0 ? 0 : ((lastVal - firstVal) / firstVal) * 100;
            }
            trends.push({
              country,
              trend,
              latestValue: years.length > 0 ? aggregate(countryByYear[years[years.length - 1]]) : aggregate(values),
              avgValue: aggregate(values),
              rank: idx + 1,
            });
          });
          return trends;
        })();
        
        const fastestGrowing = countryTrends.length > 0 ? [...countryTrends].sort((a, b) => b.trend - a.trend)[0] : null;
        const fastestDeclining = countryTrends.length > 0 ? [...countryTrends].sort((a, b) => a.trend - b.trend)[0] : null;
        const bestPerformer = topProducers[0] || null;
        
        // Dynamic labels for display
        const itemFieldValue = itemFieldName ? (filters.fields[itemFieldName] as string || '') : '';
        const elementDisplayLabel = primaryElementField?.displayName || 'Element';
        const valueDisplayLabel = valueFieldConfig?.displayName || 'Value';
        
        return (
          <div className="space-y-6">
            {/* Navigation Bar with Active Filters */}
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Active:</span>
                {itemFieldValue && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                    <Tag className="h-3.5 w-3.5" />
                    {itemFieldValue}
                  </div>
                )}
                {selectedElements.length > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    <BarChart3 className="h-3.5 w-3.5" />
                    {selectedElements.join(', ')}
                  </div>
                )}
                {filters.fromYear && filters.toYear && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    <Calendar className="h-3.5 w-3.5" />
                    {filters.fromYear} - {filters.toYear}
                  </div>
                )}
                {filters.aggregation && filters.aggregation !== 'average' && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium capitalize">
                    <Filter className="h-3.5 w-3.5" />
                    {filters.aggregation}
                  </div>
                )}
              </div>
              {/* Selected Countries as individual cards */}
              {selectedCountries.length > 0 ? (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-purple-600" />
                    <span className="text-xs font-semibold text-gray-600">{selectedCountries.length} {selectedCountries.length === 1 ? 'Country' : 'Countries'} Selected</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {selectedCountries.map((country, idx) => {
                      const countryColor = COLORS[idx % COLORS.length];
                      const countryData = mapData.find(d => d.country === country);
                      const countryTrendInfo = countryTrends.find(ct => ct.country === country);
                      const value = countryData?.value || 0;
                      const trendVal = countryTrendInfo?.trend || 0;
                      const rank = countryTrendInfo?.rank || 0;
                      return (
                        <div
                          key={country}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all hover:shadow-md cursor-default"
                          style={{ borderColor: countryColor, backgroundColor: `${countryColor}10` }}
                        >
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: countryColor }}></div>
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-gray-900 leading-tight">{country}</span>
                            <span className="text-[10px] text-gray-500">
                              {value >= 1000000 ? `${(value / 1000000).toFixed(1)}M` : value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toFixed(0)} {unitShort}
                              {rank > 0 && ` · #${rank}`}
                            </span>
                          </div>
                          {trendVal !== 0 && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${trendVal > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {trendVal > 0 ? '↑' : '↓'} {Math.abs(trendVal).toFixed(1)}%
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100">
                    <MapPin className="h-4 w-4 text-indigo-500" />
                    <span className="text-xs text-indigo-700">Click countries on the map or use the sidebar to select and compare</span>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Cards - Enhanced with real trends */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* World Total */}
              <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <Globe className="h-4 w-4 text-blue-600" />
                  <TrendingUp className={`h-3.5 w-3.5 ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-400'}`} />
                </div>
                <h3 className="text-xs font-medium text-gray-600 mb-0.5">World Total</h3>
                <p className="text-lg font-bold text-gray-900 mb-0.5">
                  {totalWorldProduction >= 1000000 
                    ? `${(totalWorldProduction / 1000000).toFixed(1)}M`
                    : totalWorldProduction >= 1000
                    ? `${(totalWorldProduction / 1000).toFixed(1)}K`
                    : totalWorldProduction.toFixed(0)
                  }
                </p>
                <p className="text-[10px] text-gray-500">{unitLabel}</p>
                {trend !== 0 && (
                  <p className={`text-[10px] font-medium mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}% over period
                  </p>
                )}
              </div>

              {/* Top Producer */}
              <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <Trophy className="h-4 w-4 text-yellow-600" />
                  {topProducer && (
                    <span className="text-[10px] font-bold text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded-full">#1</span>
                  )}
                </div>
                <h3 className="text-xs font-medium text-gray-600 mb-0.5">Top Producer</h3>
                <p className="text-base font-bold text-gray-900 mb-0.5 truncate">{topProducer?.country || 'N/A'}</p>
                <p className="text-[10px] text-gray-500">
                  {topProducer ? `${topProducer.value >= 1000000 ? `${(topProducer.value / 1000000).toFixed(1)}M` : topProducer.value >= 1000 ? `${(topProducer.value / 1000).toFixed(1)}K` : topProducer.value.toFixed(0)} ${unitShort}` : 'No data'}
                </p>
                {topProducer && totalWorldProduction > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1">{((topProducer.value / totalWorldProduction) * 100).toFixed(1)}% of world</p>
                )}
              </div>

              {/* Selected Countries */}
              <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <MapPin className="h-4 w-4 text-green-600" />
                  {selectedCountries.length > 0 && (
                    <span className="text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">{selectedCountries.length}</span>
                  )}
                </div>
                <h3 className="text-xs font-medium text-gray-600 mb-0.5">Selected</h3>
                <p className="text-lg font-bold text-gray-900 mb-0.5">{selectedCountries.length}</p>
                <p className="text-[10px] text-gray-500">countries</p>
                {selectedCountries.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1 truncate">{selectedCountries.slice(0, 2).join(', ')}{selectedCountries.length > 2 ? ` +${selectedCountries.length - 2}` : ''}</p>
                )}
              </div>

              {/* Data Points */}
              <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <BarChart3 className="h-4 w-4 text-indigo-600" />
                  <span className="text-[10px] text-gray-400">{globalData.length.toLocaleString()} records</span>
                </div>
                <h3 className="text-xs font-medium text-gray-600 mb-0.5">Data Points</h3>
                <p className="text-lg font-bold text-gray-900 mb-0.5">{yearsCount}</p>
                <p className="text-[10px] text-gray-500">years</p>
                <p className="text-[10px] text-gray-400 mt-1">{yearRange}</p>
              </div>
            </div>

            {/* Insight Cards - Decision-friendly highlights */}
            {(fastestGrowing || fastestDeclining || bestPerformer) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {bestPerformer && (
                  <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg shadow-sm p-4 border border-yellow-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                        <Trophy className="h-4 w-4 text-yellow-600" />
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-yellow-800">Top Performer</h4>
                        <p className="text-[10px] text-yellow-600">Highest {primaryElement}</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{bestPerformer.country}</p>
                    <p className="text-xs text-gray-600">
                      {bestPerformer.value >= 1000000 ? `${(bestPerformer.value / 1000000).toFixed(1)}M` : bestPerformer.value >= 1000 ? `${(bestPerformer.value / 1000).toFixed(1)}K` : bestPerformer.value.toFixed(0)} {unitShort}
                    </p>
                    {totalWorldProduction > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-[10px] mb-1">
                          <span className="text-gray-500">World share</span>
                          <span className="font-bold text-yellow-700">{((bestPerformer.value / totalWorldProduction) * 100).toFixed(1)}%</span>
                        </div>
                        <div className="h-1.5 bg-yellow-100 rounded-full overflow-hidden">
                          <div className="h-full bg-yellow-500 rounded-full" style={{ width: `${Math.min((bestPerformer.value / totalWorldProduction) * 100, 100)}%` }}></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {fastestGrowing && fastestGrowing.trend > 0 && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-sm p-4 border border-green-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-green-800">Fastest Growing</h4>
                        <p className="text-[10px] text-green-600">Highest increase</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{fastestGrowing.country}</p>
                    <p className="text-xs text-green-700 font-semibold">↑ {fastestGrowing.trend.toFixed(1)}% growth</p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Latest: {fastestGrowing.latestValue >= 1000000 ? `${(fastestGrowing.latestValue / 1000000).toFixed(1)}M` : fastestGrowing.latestValue >= 1000 ? `${(fastestGrowing.latestValue / 1000).toFixed(1)}K` : fastestGrowing.latestValue.toFixed(0)} {unitShort}
                    </p>
                  </div>
                )}
                {fastestDeclining && fastestDeclining.trend < 0 && (
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg shadow-sm p-4 border border-red-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                        <TrendingUp className="h-4 w-4 text-red-600 rotate-180" />
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-red-800">Fastest Declining</h4>
                        <p className="text-[10px] text-red-600">Largest decrease</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{fastestDeclining.country}</p>
                    <p className="text-xs text-red-700 font-semibold">↓ {Math.abs(fastestDeclining.trend).toFixed(1)}% decline</p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Latest: {fastestDeclining.latestValue >= 1000000 ? `${(fastestDeclining.latestValue / 1000000).toFixed(1)}M` : fastestDeclining.latestValue >= 1000 ? `${(fastestDeclining.latestValue / 1000).toFixed(1)}K` : fastestDeclining.latestValue.toFixed(0)} {unitShort}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Selected Countries Comparison Cards */}
            {selectedCountries.length > 1 && countryTrends.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-indigo-600" />
                    <h3 className="text-sm font-bold text-gray-900">Country Comparison</h3>
                  </div>
                  <span className="text-[10px] text-gray-500">{primaryElement} · {yearRange}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {selectedCountries.map((country, idx) => {
                    const countryColor = COLORS[idx % COLORS.length];
                    const ct = countryTrends.find(c => c.country === country);
                    const countryData = mapData.find(d => d.country === country);
                    const value = countryData?.value || ct?.avgValue || 0;
                    const trendVal = ct?.trend || 0;
                    const rank = ct?.rank || 0;
                    const worldShare = totalWorldProduction > 0 && value > 0 ? (value / totalWorldProduction) * 100 : 0;
                    
                    return (
                      <div key={country} className="rounded-xl border-2 p-3 transition-all hover:shadow-md" style={{ borderColor: `${countryColor}60`, backgroundColor: `${countryColor}08` }}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: countryColor }}></div>
                          <span className="text-sm font-bold text-gray-900 truncate flex-1">{country}</span>
                          {rank > 0 && <span className="text-[10px] font-bold text-gray-400">#{rank}</span>}
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-gray-500">Value</span>
                            <span className="text-xs font-bold text-gray-900">
                              {value >= 1000000 ? `${(value / 1000000).toFixed(1)}M` : value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toFixed(0)} {unitShort}
                            </span>
                          </div>
                          {worldShare > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] text-gray-500">World share</span>
                                <span className="text-[10px] font-bold" style={{ color: countryColor }}>{worldShare.toFixed(1)}%</span>
                              </div>
                              <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${Math.min(worldShare * 3, 100)}%`, backgroundColor: countryColor }}></div>
                              </div>
                            </div>
                          )}
                          {trendVal !== 0 && (
                            <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                              <span className="text-[10px] text-gray-500">Trend</span>
                              <span className={`text-[10px] font-bold ${trendVal > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {trendVal > 0 ? '↑' : '↓'} {Math.abs(trendVal).toFixed(1)}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Compact Map and Top Producers Row - Reduced Size */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Compact Map Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-2.5 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900 mb-1">{getVisualizationTitle() || `Global ${primaryElement} Distribution`}</h3>
                      <p className="text-[10px] text-gray-600">{getVisualizationSubtitle()}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {itemFieldValue && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-indigo-100 text-indigo-800">
                            {primaryItemField?.displayName || 'Item'}: {itemFieldValue}
                          </span>
                        )}
                        {selectedElements.length > 0 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-800">
                            {elementDisplayLabel}: {selectedElements.length}
                          </span>
                        )}
                        {filters.fromYear && filters.toYear && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-100 text-green-800">
                            {filters.fromYear} - {filters.toYear}
                          </span>
                        )}
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-100 text-purple-800">
                          {filters.aggregation ? filters.aggregation.charAt(0).toUpperCase() + filters.aggregation.slice(1) : 'Average'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Export functionality can be added here
                        }}
                        className="p-1 rounded hover:bg-gray-100 transition-colors"
                        title="Export"
                      >
                        <Download className="h-3 w-3 text-gray-600" />
                      </button>
                      <button 
                        onClick={() => setExpandedMap(true)}
                        className="p-1 rounded hover:bg-gray-100 transition-colors"
                        title="Expand"
                      >
                        <Maximize2 className="h-3.5 w-3.5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="h-[200px] relative cursor-pointer">
                  {!expandedMap && !expandedChart && !expandedTopProducers && (
                    <MapContainer 
                      key="compact-map"
                      center={[20, 0]} 
                      zoom={2} 
                      style={{ height: '100%', width: '100%' }} 
                      scrollWheelZoom={false} 
                      zoomControl={true}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                      {worldGeoJSON && mapData.length > 0 && (
                        <GeoJSON key={`geojson-compact-${mapData.length}`} data={worldGeoJSON} style={getGeoJSONStyle} onEachFeature={onEachCountry} />
                      )}
                    </MapContainer>
                  )}
                  <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-medium text-indigo-700 shadow-sm border border-indigo-100">
                    🖱 Click a country to select
                  </div>
                  <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-medium text-gray-700 shadow-sm">
                    {mapData.length} countries
                  </div>
                </div>
                {/* Compact Legend */}
                <div className="p-2 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-1">
                      <Filter className="h-3 w-3 text-gray-600" />
                      <span className="text-[10px] font-semibold text-gray-700">{primaryElement} Scale ({unitShort})</span>
                    </div>
                    <span className="text-[9px] text-gray-500">
                      {mapData.length} countries
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <span className="text-[9px] text-gray-600 font-medium">Low</span>
                      <div className="flex space-x-0.5">
                        <div className="w-4 h-4 bg-yellow-200 rounded-l border border-gray-400"></div>
                        <div className="w-4 h-4 bg-yellow-400 border-t border-b border-gray-400"></div>
                        <div className="w-4 h-4 bg-orange-500 border-t border-b border-gray-400"></div>
                        <div className="w-4 h-4 bg-red-500 border-t border-b border-gray-400"></div>
                        <div className="w-4 h-4 bg-red-700 rounded-r border border-gray-400"></div>
                      </div>
                      <span className="text-[9px] text-gray-600 font-medium">High</span>
                    </div>
                    <div className="text-[9px] text-gray-500">
                      Max: <span className="font-semibold text-gray-700">{maxMapValue.toLocaleString()} {unitShort}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compact Top Producers Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => setExpandedTopProducers(true)}>
                <div className="p-2.5 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Top {primaryElement} Producers</h3>
                    <p className="text-[10px] text-gray-600 mt-0.5">{yearRange} {aggregationFunc}</p>
                  </div>
                  <Maximize2 className="h-4 w-4 text-gray-400" />
                </div>
                <div className="p-2.5 max-h-[200px] overflow-y-auto">
                  <div className="space-y-1.5">
                    {topProducers.slice(0, 5).map((producer, index) => {
                      const maxValue = topProducers[0].value;
                      const percentage = (producer.value / maxValue) * 100;
                      const color = COLORS[index % COLORS.length];
                      return (
                        <div key={producer.country} className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: color }}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 truncate">{producer.country}</div>
                            <div className="relative h-1.5 bg-gray-100 rounded-full mt-0.5">
                              <div className="absolute h-full rounded-full" style={{ width: `${percentage}%`, backgroundColor: color }}></div>
                            </div>
                          </div>
                          <div className="text-[10px] font-bold text-gray-700 whitespace-nowrap">
                            {producer.value >= 1000000 ? `${(producer.value / 1000000).toFixed(1)}M` : producer.value >= 1000 ? `${(producer.value / 1000).toFixed(1)}K` : producer.value.toFixed(0)} {unitShort}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-center">
                    <button className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                      +{topProducers.length - 5} more countries
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Country Selector (still accessible) */}
            {showCountrySelector && mapData.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">Select Countries for Comparison</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllVisibleCountries}
                      className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 border border-indigo-300 rounded hover:bg-indigo-50 transition-colors"
                    >
                      Select All Visible
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCountries([]);
                        setCountryTimeSeries({});
                        setCountryStats({});
                      }}
                      className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    placeholder="Search countries..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
                <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 rounded-md bg-white">
                  <div className="p-2 space-y-1">
                    {getFilteredCountries().map((country) => {
                      const isSelected = selectedCountries.includes(country);
                      const countryData = mapData.find(d => d.country === country);
                      const value = countryData?.value || 0;
                      const index = selectedCountries.indexOf(country);
                      const color = index >= 0 ? COLORS[index % COLORS.length] : '#gray';
                      return (
                        <div
                          key={country}
                          onClick={() => toggleCountrySelection(country)}
                          className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-indigo-50 border-2 border-indigo-300'
                              : 'hover:bg-gray-50 border-2 border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-indigo-600" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-400" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {isSelected && (
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                                )}
                                <span className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-700'}`}>
                                  {country}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {primaryElement}: {value.toLocaleString()} {unitShort}
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <div className="text-xs text-indigo-600 font-medium">#{index + 1}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Element Charts and Country Analysis - Keep existing */}

            {/* Element Charts - One chart per element with all selected countries */}
            {selectedCountries.length > 0 && Object.keys(elementTimeSeries).length > 0 && (
              <div className="space-y-8">
                {Object.keys(elementTimeSeries).map((element, elementIndex) => {
                  const elementData = elementTimeSeries[element];
                  const countriesWithData = selectedCountries.filter(country => 
                    elementData[country] && elementData[country].length > 0
                  );
                  
                  if (countriesWithData.length === 0) return null;
                  
                  // Get all unique years across all countries for this element
                  const allYears = new Set<string>();
                  countriesWithData.forEach(country => {
                    elementData[country].forEach(point => {
                      allYears.add(point.name);
                    });
                  });
                  
                  const sortedYears = Array.from(allYears).sort((a, b) => parseInt(a) - parseInt(b));
                  
                  // Prepare chart data: one data point per year with values for each country
                  const chartData = sortedYears.map(year => {
                    const dataPoint: any = { name: year };
                    countriesWithData.forEach(country => {
                      const countryData = elementData[country].find(p => p.name === year);
                      dataPoint[country] = countryData ? countryData.value : null;
                    });
                    return dataPoint;
                  });
                  
                  // Check if element is a main element (dynamic from filterFieldConfigs)
                  const isMainElement = filterFieldConfigs.some(f => f.isValueField || (!f.isAreaField && !f.isYearField && f.fieldType === 'keyword' && !f.isItemLike)) && 
                    filterFieldConfigs.filter(f => !f.isAreaField && !f.isYearField && f.fieldType === 'keyword' && !f.isItemLike).some(f => element.toLowerCase().includes(f.displayName.toLowerCase()));
                  
                  return (
                    <div key={element} className="bg-white rounded-lg shadow-md p-3 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col">
                      {/* Enhanced Header with Tabs */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {/* Element Tab */}
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-lg shadow-sm">
                              <span className="font-bold text-sm">{element}</span>
                              {isMainElement && (
                                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold backdrop-blur-sm">
                                  Main
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="font-semibold text-gray-900">{itemFieldValue || selectedIndex}</span>
                            <span>•</span>
                            <span>{filters.fromYear || 'Start'} - {filters.toYear || 'End'}</span>
                            <span>•</span>
                            <span className="capitalize">{filters.aggregation || 'average'}</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button 
                            onClick={() => {
                              const elementData = elementTimeSeries[element];
                              const countriesWithData = selectedCountries.filter(country => 
                                elementData[country] && elementData[country].length > 0
                              );
                              
                              if (countriesWithData.length === 0) return;
                              
                              const allYears = new Set<string>();
                              countriesWithData.forEach(country => {
                                elementData[country].forEach(point => {
                                  allYears.add(point.name);
                                });
                              });
                              
                              const sortedYears = Array.from(allYears).sort((a, b) => parseInt(a) - parseInt(b));
                              
                              const chartData = sortedYears.map(year => {
                                const dataPoint: any = { name: year };
                                countriesWithData.forEach(country => {
                                  const countryData = elementData[country].find(p => p.name === year);
                                  dataPoint[country] = countryData ? countryData.value : null;
                                });
                                return dataPoint;
                              });
                              
                              const chartColors = countriesWithData.map((_, idx) => COLORS[idx % COLORS.length]);
                              setExpandedChart({
                                type: 'element',
                                key: element,
                                title: element,
                                subtitle: `${itemFieldValue || selectedIndex} • ${filters.fromYear || 'Start'} - ${filters.toYear || 'End'} • ${filters.aggregation || 'average'}`,
                                chartData,
                                dataKeys: countriesWithData,
                                colors: chartColors,
                              });
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Fullscreen"
                          >
                            <Maximize2 className="h-4 w-4 text-gray-600" />
                          </button>
                          <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                            <Download className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                      
                      {(() => {
                        const elementData = elementTimeSeries[element];
                        const countriesWithData = selectedCountries.filter(country => 
                          elementData[country] && elementData[country].length > 0
                        );
                        
                        if (countriesWithData.length === 0) return null;
                        
                        const allYears = new Set<string>();
                        countriesWithData.forEach(country => {
                          elementData[country].forEach(point => {
                            allYears.add(point.name);
                          });
                        });
                        
                        const sortedYears = Array.from(allYears).sort((a, b) => parseInt(a) - parseInt(b));
                        
                        const chartData = sortedYears.map(year => {
                          const dataPoint: any = { name: year };
                          countriesWithData.forEach(country => {
                            const countryData = elementData[country].find(p => p.name === year);
                            dataPoint[country] = countryData ? countryData.value : null;
                          });
                          return dataPoint;
                        });
                        
                        // Dynamic main element detection from filterFieldConfigs
                        const isMainElement = filterFieldConfigs.filter(f => !f.isAreaField && !f.isYearField && f.fieldType === 'keyword' && !f.isItemLike).some(f => element.toLowerCase().includes(f.displayName.toLowerCase()));
                        
                        return (
                          <>
                            {chartData.length > 0 ? (
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-3 border border-gray-100">
                          <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                              <defs>
                                {countriesWithData.map((country, countryIndex) => {
                                  const countryColor = COLORS[countryIndex % COLORS.length];
                                  return (
                                    <linearGradient key={country} id={`color${countryIndex}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor={countryColor} stopOpacity={0.3}/>
                                      <stop offset="95%" stopColor={countryColor} stopOpacity={0.05}/>
                                    </linearGradient>
                                  );
                                })}
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                              <XAxis 
                                dataKey="name" 
                                stroke="#6b7280"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: '#6b7280' }}
                              />
                              <YAxis 
                                stroke="#6b7280"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: '#6b7280' }}
                                tickFormatter={(value) => {
                                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                                  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
                                  return value.toString();
                                }}
                              />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                  padding: '12px',
                                }}
                                formatter={(value: any, name: string) => {
                                  if (value === null || value === undefined) return ['No data', name];
                                  return [typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value, name];
                                }}
                                labelFormatter={(label) => `Year: ${label}`}
                              />
                              <Legend 
                                wrapperStyle={{ paddingTop: '20px', paddingBottom: '10px' }}
                                iconType="circle"
                                iconSize={10}
                              />
                              {countriesWithData.map((country, countryIndex) => {
                                const countryColor = COLORS[countryIndex % COLORS.length];
                                return (
                                  <Area
                                    key={country}
                                    type="monotone"
                                    dataKey={country}
                                    name={country}
                                    stroke={countryColor}
                                    strokeWidth={3}
                                    fill={`url(#color${countryIndex})`}
                                    fillOpacity={1}
                                    dot={{ r: 5, fill: countryColor, strokeWidth: 2, stroke: 'white' }}
                                    activeDot={{ r: 8, fill: countryColor, strokeWidth: 2, stroke: 'white' }}
                                  />
                                );
                              })}
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg border border-gray-200">
                          <Database className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                          <p className="text-lg font-medium">No data available for {element} in the selected period</p>
                        </div>
                      )}
                      
                      {/* Enhanced Summary Statistics */}
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <div className="w-1 h-6 bg-gradient-to-b from-indigo-500 to-blue-600 rounded-full"></div>
                          Summary by Country
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {countriesWithData.map((country, countryIndex) => {
                            const countryData = elementData[country];
                            const values = countryData.map(p => p.value).filter(v => v !== null && v !== undefined);
                            if (values.length === 0) return null;
                            
                            const total = values.reduce((a, b) => a + b, 0);
                            const average = total / values.length;
                            const min = Math.min(...values);
                            const max = Math.max(...values);
                            const trend = countryData.length > 1 
                              ? ((countryData[countryData.length - 1].value - countryData[0].value) / countryData[0].value * 100)
                              : 0;
                            
                            const countryColor = COLORS[countryIndex % COLORS.length];
                            
                            return (
                              <div key={country} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
                                  <div 
                                    className="w-4 h-4 rounded-full shadow-sm"
                                    style={{ backgroundColor: countryColor }}
                                  ></div>
                                  <h5 className="font-bold text-gray-900">{country}</h5>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div className="bg-gray-50 rounded-lg p-2">
                                    <div className="text-xs text-gray-500 mb-1">Average</div>
                                    <div className="font-bold text-gray-900">{average.toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
                                  </div>
                                  <div className="bg-gray-50 rounded-lg p-2">
                                    <div className="text-xs text-gray-500 mb-1">Total</div>
                                    <div className="font-bold text-gray-900">{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                  </div>
                                  <div className="bg-gray-50 rounded-lg p-2">
                                    <div className="text-xs text-gray-500 mb-1">Min</div>
                                    <div className="font-bold text-gray-900">{min.toLocaleString()}</div>
                                  </div>
                                  <div className="bg-gray-50 rounded-lg p-2">
                                    <div className="text-xs text-gray-500 mb-1">Max</div>
                                    <div className="font-bold text-gray-900">{max.toLocaleString()}</div>
                                  </div>
                                  {trend !== 0 && (
                                    <div className="col-span-2 mt-2 pt-3 border-t border-gray-200">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-500">Trend</span>
                                        <span className={`font-bold text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                          </>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Country Charts - One chart per country with all selected elements - Grid Layout */}
            {selectedCountries.length > 0 && Object.keys(elementTimeSeries).length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Country Analysis</h2>
                  <p className="text-sm text-gray-600">
                    Individual charts for each selected country
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedCountries.map((country, countryIndex) => {
                  const countryColor = COLORS[countryIndex % COLORS.length];
                  
                  // Get all elements that have data for this country
                  const elementsWithData = Object.keys(elementTimeSeries).filter(element => 
                    elementTimeSeries[element][country] && elementTimeSeries[element][country].length > 0
                  );
                  
                  if (elementsWithData.length === 0) {
                    return (
                      <div key={country} className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                        <div className="text-center text-gray-500 py-8">
                          <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p>No data available for {country} in the selected period</p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Get all unique years across all elements for this country
                  const allYears = new Set<string>();
                  elementsWithData.forEach(element => {
                    elementTimeSeries[element][country].forEach(point => {
                      allYears.add(point.name);
                    });
                  });
                  
                  const sortedYears = Array.from(allYears).sort((a, b) => parseInt(a) - parseInt(b));
                  
                  // Prepare chart data: one data point per year with values for each element
                  const chartData = sortedYears.map(year => {
                    const dataPoint: any = { name: year };
                    elementsWithData.forEach(element => {
                      const elementData = elementTimeSeries[element][country].find(p => p.name === year);
                      dataPoint[element] = elementData ? elementData.value : null;
                    });
                    return dataPoint;
                  });
                  
                  // Calculate statistics for this country across all elements
                  const countryStats: { [element: string]: any } = {};
                  elementsWithData.forEach(element => {
                    const elementData = elementTimeSeries[element][country];
                    const values = elementData.map(p => p.value).filter(v => v !== null && v !== undefined);
                    if (values.length > 0) {
                      const total = values.reduce((a, b) => a + b, 0);
                      const average = total / values.length;
                      const min = Math.min(...values);
                      const max = Math.max(...values);
                      const trend = elementData.length > 1 
                        ? ((elementData[elementData.length - 1].value - elementData[0].value) / elementData[0].value * 100)
                        : 0;
                      
                      countryStats[element] = {
                        total,
                        average,
                        min,
                        max,
                        trend,
                        count: values.length,
                      };
                    }
                  });
                  
                  return (
                    <div key={country} className="bg-white rounded-lg shadow-md p-3 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col">
                      {/* Enhanced Header */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            {/* Country Tab */}
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-md shadow-sm">
                              <span className="font-bold text-xs truncate">{country}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-gray-600 flex-wrap">
                            <span className="font-semibold text-gray-900 truncate">{itemFieldValue || selectedIndex}</span>
                            <span>•</span>
                            <span className="truncate">{filters.fromYear || 'Start'} - {filters.toYear || 'End'}</span>
                            <span>•</span>
                            <span className="capitalize">{filters.aggregation || 'average'}</span>
                          </div>
                        </div>
                        <div className="flex gap-1 ml-1">
                          <button 
                            onClick={() => {
                              const chartColors = elementsWithData.map((_, idx) => COLORS[idx % COLORS.length]);
                              setExpandedChart({
                                type: 'country',
                                key: country,
                                title: country,
                                subtitle: `${itemFieldValue || selectedIndex} • ${filters.fromYear || 'Start'} - ${filters.toYear || 'End'} • ${filters.aggregation || 'average'}`,
                                chartData,
                                dataKeys: elementsWithData,
                                colors: chartColors,
                              });
                            }}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title="Fullscreen"
                          >
                            <Maximize2 className="h-3.5 w-3.5 text-gray-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                            <Download className="h-3.5 w-3.5 text-gray-600" />
                          </button>
                        </div>
                      </div>
                      
                      {chartData.length > 0 ? (
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-2 border border-gray-100 flex-1 min-h-[180px]">
                          <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                              <defs>
                                {elementsWithData.map((element, elementIndex) => {
                                  const elementColor = COLORS[elementIndex % COLORS.length];
                                  // Dynamic main element detection from filterFieldConfigs
                                  const isMainElement = filterFieldConfigs.filter(f => !f.isAreaField && !f.isYearField && f.fieldType === 'keyword' && !f.isItemLike).some(f => element.toLowerCase().includes(f.displayName.toLowerCase()));
                                  return (
                                    <linearGradient key={element} id={`countryColor${countryIndex}Element${elementIndex}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor={elementColor} stopOpacity={isMainElement ? 0.3 : 0.2}/>
                                      <stop offset="95%" stopColor={elementColor} stopOpacity={isMainElement ? 0.05 : 0.02}/>
                                    </linearGradient>
                                  );
                                })}
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                              <XAxis 
                                dataKey="name" 
                                stroke="#6b7280"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: '#6b7280' }}
                              />
                              <YAxis 
                                stroke="#6b7280"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                tick={{ fill: '#6b7280' }}
                                tickFormatter={(value) => {
                                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                                  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
                                  return value.toString();
                                }}
                              />
                              <Tooltip 
                                contentStyle={{
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                  padding: '12px',
                                }}
                                formatter={(value: any, name: string) => {
                                  if (value === null || value === undefined) return ['No data', name];
                                  return [typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value, name];
                                }}
                                labelFormatter={(label) => `Year: ${label}`}
                              />
                              <Legend 
                                wrapperStyle={{ paddingTop: '20px', paddingBottom: '10px' }}
                                iconType="circle"
                                iconSize={10}
                              />
                              {elementsWithData.map((element, elementIndex) => {
                                const elementColor = COLORS[elementIndex % COLORS.length];
                                // Dynamic main element detection from filterFieldConfigs
                                const isMainElement = filterFieldConfigs.filter(f => !f.isAreaField && !f.isYearField && f.fieldType === 'keyword' && !f.isItemLike).some(f => element.toLowerCase().includes(f.displayName.toLowerCase()));
                                
                                return (
                                  <Area
                                    key={element}
                                    type="monotone"
                                    dataKey={element}
                                    name={element}
                                    stroke={elementColor}
                                    strokeWidth={isMainElement ? 3 : 2}
                                    fill={`url(#countryColor${countryIndex}Element${elementIndex})`}
                                    fillOpacity={1}
                                    dot={{ r: isMainElement ? 5 : 4, fill: elementColor, strokeWidth: 2, stroke: 'white' }}
                                    activeDot={{ r: 8, fill: elementColor, strokeWidth: 2, stroke: 'white' }}
                                    strokeDasharray={isMainElement ? undefined : '5 5'}
                                  />
                                );
                              })}
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg border border-gray-200">
                          <Database className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                          <p className="text-lg font-medium">No data available for {country} in the selected period</p>
                        </div>
                      )}
                      
                      {/* Enhanced Summary Statistics by Element for this Country - Compact */}
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <h4 className="text-xs font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                          <div className="w-0.5 h-4 rounded-full" style={{ background: `linear-gradient(to bottom, ${countryColor}, ${countryColor}dd)` }}></div>
                          Summary by Element
                        </h4>
                        <div className="space-y-2">
                          {elementsWithData.map((element, elementIndex) => {
                            const stats = countryStats[element];
                            if (!stats) return null;
                            
                            const elementColor = COLORS[elementIndex % COLORS.length];
                            // Dynamic main element detection from filterFieldConfigs
                            const isMainElement = filterFieldConfigs.filter(f => !f.isAreaField && !f.isYearField && f.fieldType === 'keyword' && !f.isItemLike).some(f => element.toLowerCase().includes(f.displayName.toLowerCase()));
                            
                            return (
                              <div key={element} className={`rounded-lg p-2.5 border shadow-sm hover:shadow-md transition-shadow ${isMainElement ? 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'}`}>
                                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
                                  <div 
                                    className="w-2.5 h-2.5 rounded-full shadow-sm flex-shrink-0"
                                    style={{ backgroundColor: elementColor }}
                                  ></div>
                                  <h5 className={`font-bold text-[10px] flex-1 truncate ${isMainElement ? 'text-blue-900' : 'text-gray-900'}`}>
                                    {element}
                                  </h5>
                                  {isMainElement && (
                                    <span className="px-1 py-0.5 bg-blue-200 text-blue-800 rounded-full text-[9px] font-bold shadow-sm flex-shrink-0">
                                      Main
                                    </span>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-[10px]">
                                  <div className={`rounded-md p-1.5 ${isMainElement ? 'bg-blue-100/50' : 'bg-gray-50'}`}>
                                    <div className="text-[9px] text-gray-500 mb-0.5">Avg</div>
                                    <div className="font-bold text-gray-900 text-[10px]">{stats.average.toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
                                  </div>
                                  <div className={`rounded-md p-1.5 ${isMainElement ? 'bg-blue-100/50' : 'bg-gray-50'}`}>
                                    <div className="text-[9px] text-gray-500 mb-0.5">Total</div>
                                    <div className="font-bold text-gray-900 text-[10px]">{stats.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                  </div>
                                  <div className={`rounded-md p-1.5 ${isMainElement ? 'bg-blue-100/50' : 'bg-gray-50'}`}>
                                    <div className="text-[9px] text-gray-500 mb-0.5">Min</div>
                                    <div className="font-bold text-gray-900 text-[10px]">{stats.min.toLocaleString()}</div>
                                  </div>
                                  <div className={`rounded-md p-1.5 ${isMainElement ? 'bg-blue-100/50' : 'bg-gray-50'}`}>
                                    <div className="text-[9px] text-gray-500 mb-0.5">Max</div>
                                    <div className="font-bold text-gray-900 text-[10px]">{stats.max.toLocaleString()}</div>
                                  </div>
                                </div>
                                {stats.trend !== 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[9px] text-gray-600">Trend</span>
                                      <span className={`text-[10px] font-bold ${stats.trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {stats.trend > 0 ? '↑' : '↓'} {Math.abs(stats.trend).toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
                
                {/* Clear All Button */}
                {selectedCountries.length > 0 && (
                  <div className="flex justify-center pt-4">
                    <button
                      onClick={() => {
                        setSelectedCountries([]);
                        setCountryTimeSeries({});
                        setElementTimeSeries({});
                        setCountryStats({});
                      }}
                      className="text-sm text-red-600 hover:text-red-800 px-6 py-2 border-2 border-red-300 rounded-md hover:bg-red-50 transition-colors font-medium"
                    >
                      Clear All Country Selections
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

          {/* Empty State */}
          {mapData.length === 0 && initialLoading && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <RefreshCw className="mx-auto h-12 w-12 text-blue-600 mb-4 animate-spin" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Loading data...</h3>
              <p className="text-gray-500">Setting up default filters and fetching data.</p>
            </div>
          )}
          {mapData.length === 0 && !loading && !initialLoading && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No data to visualize</h3>
              <p className="text-gray-500">Select filters and click "Visualize" to see results.</p>
            </div>
          )}

      {/* Expanded Map Modal */}
      {expandedMap && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedMap(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Global {_primaryElement} Distribution</h3>
                  <p className="text-sm text-gray-600 mt-1">{getVisualizationSubtitle()}</p>
                </div>
                <button
                  onClick={() => setExpandedMap(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>
              {selectedCountries.length > 0 && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-gray-500">{selectedCountries.length} selected:</span>
                  {selectedCountries.map((country, idx) => (
                    <span
                      key={country}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
                      style={{ borderColor: COLORS[idx % COLORS.length], backgroundColor: `${COLORS[idx % COLORS.length]}15`, color: COLORS[idx % COLORS.length] }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                      {country}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6">
              <div className="h-[600px] rounded-lg overflow-hidden border-2 border-gray-300 shadow-inner relative">
                {expandedMap && (
                  <MapContainer 
                    key="expanded-map-modal"
                    center={[20, 0]} 
                    zoom={2} 
                    style={{ height: '100%', width: '100%' }} 
                    scrollWheelZoom={true} 
                    zoomControl={true}
                  >
                    <MapUpdater isExpanded={expandedMap} />
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                    {worldGeoJSON && mapData.length > 0 && (
                      <GeoJSON key={`geojson-${mapData.length}`} data={worldGeoJSON} style={getGeoJSONStyle} onEachFeature={onEachCountry} />
                    )}
                  </MapContainer>
                )}
                <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-700 shadow-md border border-indigo-100 z-[1000]">
                  🖱 Click a country to select/deselect it
                </div>
              </div>
              <div className="mt-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-700">{_primaryElement} Scale ({_unitShort})</span>
                  </div>
                  <span className="text-xs text-gray-500">{mapData.length} countries with data</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600 font-medium">Low</span>
                    <div className="flex space-x-0.5">
                      <div className="w-6 h-6 bg-yellow-200 rounded-l border border-gray-400"></div>
                      <div className="w-6 h-6 bg-yellow-400 border-t border-b border-gray-400"></div>
                      <div className="w-6 h-6 bg-orange-500 border-t border-b border-gray-400"></div>
                      <div className="w-6 h-6 bg-red-500 border-t border-b border-gray-400"></div>
                      <div className="w-6 h-6 bg-red-700 rounded-r border border-gray-400"></div>
                    </div>
                    <span className="text-xs text-gray-600 font-medium">High</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Max: <span className="font-semibold text-gray-700">{maxMapValue.toLocaleString()} {_unitShort}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Top Producers Modal */}
      {expandedTopProducers && (() => {
        const dataSource = globalResults.length > 0 ? globalResults : allResults;
        const _areaFieldConfig = filterFieldConfigs.find(f => f.isAreaField);
        const _yearFieldConfig = filterFieldConfigs.find(f => f.isYearField);
        const _valueFieldConfig = filterFieldConfigs.find(f => f.isValueField);
        const _primaryElementField = filterFieldConfigs.find(f => !f.isAreaField && !f.isYearField && !f.isValueField && f.fieldType === 'keyword' && !f.isItemLike);
        const _primaryItemField = filterFieldConfigs.find(f => f.isItemLike);
        
        const _getFieldValue = (result: any, fieldName: string): any => {
          if (!fieldName) return '';
          if (result[fieldName] !== undefined) return result[fieldName];
          const lower = fieldName.toLowerCase();
          if (result[lower] !== undefined) return result[lower];
          const cap = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
          if (result[cap] !== undefined) return result[cap];
          return '';
        };
        
        const elementFieldName = _primaryElementField?.fieldName || '';
        const areaFieldName = _areaFieldConfig?.fieldName || '';
        const yearFieldName = _yearFieldConfig?.fieldName || '';
        const valueFieldName = _valueFieldConfig?.fieldName || '';
        
        const selectedElements = elementFieldName ? (Array.isArray(filters.fields[elementFieldName]) ? filters.fields[elementFieldName] as string[] : filters.fields[elementFieldName] ? [filters.fields[elementFieldName] as string] : []) : [];
        const fromYear = filters.fromYear ? parseInt(filters.fromYear) : null;
        const toYear = filters.toYear ? parseInt(filters.toYear) : null;
        const aggregationFunc = filters.aggregation || 'average';
        
        const globalData = dataSource.filter((result: any) => {
          const element = _primaryElementField ? _getFieldValue(result, elementFieldName) : (result.element || result.Element || '') as string;
          const year = _yearFieldConfig ? _getFieldValue(result, yearFieldName) : (result.year || result.Year || '') as string;
          if (selectedElements.length > 0 && !selectedElements.includes(element)) return false;
          if (year) {
            const yearNum = parseInt(String(year));
            if (fromYear && yearNum < fromYear) return false;
            if (toYear && yearNum > toYear) return false;
          }
          return true;
        });
        
        const byCountry: { [key: string]: number[] } = {};
        globalData.forEach((result: any) => {
          const area = _areaFieldConfig ? _getFieldValue(result, areaFieldName) : (result.area || result.Area || 'Unknown') as string;
          const value = parseFloat(String(_valueFieldConfig ? _getFieldValue(result, valueFieldName) : (result.value || result.Value || '0')));
          if (area && area !== 'Unknown' && !isNaN(value) && value > 0) {
            if (!byCountry[area]) byCountry[area] = [];
            byCountry[area].push(value);
          }
        });
        
        const aggregate = (values: number[]) => {
          if (values.length === 0) return 0;
          switch (aggregationFunc) {
            case 'sum': return values.reduce((a, b) => a + b, 0);
            case 'min': return Math.min(...values);
            case 'max': return Math.max(...values);
            case 'average':
            default: return values.reduce((a, b) => a + b, 0) / values.length;
          }
        };
        
        const topProducers = Object.keys(byCountry)
          .map(country => ({ country, value: aggregate(byCountry[country]) }))
          .filter(d => d.value > 0)
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);
        
        const primaryElement = selectedElements.length > 0 ? selectedElements[0] : (_primaryElementField?.displayName || 'Value');
        const _unitLabel = dataSource.length > 0 ? (_getFieldValue(dataSource[0], 'Unit') || _valueFieldConfig?.displayName || 'Value') : 'Value';
        const _unitShort = _unitLabel.length > 3 ? _unitLabel.substring(0, 3) : _unitLabel;
        const yearRange = filters.fromYear && filters.toYear ? `${filters.fromYear} - ${filters.toYear}` : filters.fromYear ? `From ${filters.fromYear}` : filters.toYear ? `Until ${filters.toYear}` : 'All Years';
        
        return (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setExpandedTopProducers(false)}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Top {primaryElement} Producers</h3>
                  <p className="text-sm text-gray-600 mt-1">{yearRange} {aggregationFunc}</p>
                </div>
                <button
                  onClick={() => setExpandedTopProducers(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {topProducers.map((producer, index) => {
                    const maxValue = topProducers[0].value;
                    const percentage = (producer.value / maxValue) * 100;
                    const color = COLORS[index % COLORS.length];
                    return (
                      <div key={producer.country} className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md transition-all">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-md" style={{ backgroundColor: color }}>
                          {index + 1}
                        </div>
                        <div className="min-w-[200px]">
                          <span className="font-semibold text-lg text-gray-900">{producer.country}</span>
                        </div>
                        <div className="flex-1">
                          <div className="relative h-12 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                            <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${percentage}%`, background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)` }}></div>
                            <div className="absolute inset-0 flex items-center justify-end pr-4">
                              <span className="text-base font-bold text-gray-800">
                                {producer.value >= 1000000 ? `${(producer.value / 1000000).toFixed(1)}M ${_unitShort}` : producer.value >= 1000 ? `${(producer.value / 1000).toFixed(1)}K ${_unitShort}` : `${producer.value.toFixed(0)} ${_unitShort}`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Expanded Chart Modal */}
      {expandedChart && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedChart(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{expandedChart.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{expandedChart.subtitle}</p>
              </div>
              <button
                onClick={() => setExpandedChart(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6">
              <div className="relative">
                <ResponsiveContainer width="100%" height={600}>
                  <AreaChart data={expandedChart.chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    {expandedChart.dataKeys.map((key: string, index: number) => {
                      const color = expandedChart.colors[index];
                      return (
                        <linearGradient key={key} id={`expandedColor${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={color} stopOpacity={0.05}/>
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                  <XAxis 
                    dataKey="name" 
                    stroke="#6b7280"
                    fontSize={14}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#6b7280' }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={14}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: '#6b7280' }}
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
                      return value.toString();
                    }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      padding: '12px',
                    }}
                    formatter={(value: any, name: string) => {
                      if (value === null || value === undefined) return ['No data', name];
                      return [typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value, name];
                    }}
                    labelFormatter={(label) => `Year: ${label}`}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px', paddingBottom: '10px' }}
                    iconType="circle"
                    iconSize={10}
                  />
                  {expandedChart.dataKeys.map((key: string, index: number) => {
                    const color = expandedChart.colors[index];
                    return (
                      <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={key}
                        stroke={color}
                        strokeWidth={3}
                        fill={`url(#expandedColor${index})`}
                        fillOpacity={1}
                        dot={{ r: 5, fill: color, strokeWidth: 2, stroke: 'white' }}
                        activeDot={{ r: 8, fill: color, strokeWidth: 2, stroke: 'white' }}
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default DataVisualization;
